// components/Chat.tsx
import React, { memo, useCallback, useState, useEffect, useMemo, useRef, useContext } from 'react';
import { isFactoryReferenceLookupScript } from '#lib/list/factoryReferenceLookup';
import { createChatMessageKeyer } from '#lib/chat/chatMessageKeys';
import { BlurRevealImage, useBlurRevealFilter } from './BlurRevealImage';
import { designCommandTimeEstimations } from './designCommands';
import { videoLoopStageLabel } from '#lib/video-loop-tiers';
import ReactMarkdown from 'react-markdown';
import { BrandResearchResult } from '@/components/slides/BrandResearchResult';
import { WebBrowsingSegment } from '@/components/WebBrowsingSegment';
import type { WebBrowsingState, WebSource, WebBrowsingPhase } from '@/lib/web/webBrowsing';
import { domainFromUrl, googleFaviconUrl } from '@/lib/web/favicon';
import remarkGfm from 'remark-gfm';
import { ChatAnalystTableCard } from '@/components/chat/ChatAnalystTableCard';
import { ChatSourceTableCard } from '@/components/chat/ChatSourceTableCard';
import { SourceBudgetProposalCard } from '@/components/source/SourceBudgetProposalCard';
import { readSourceBudgetProposal } from '#lib/leasing/sourceBudgetProposal';
import { isSourceSlashCommandText } from '@/components/source/sourceSlashCommands';
import { isPlanSlashCommandText } from '@/components/footfall/planSlashCommands';
import { isFactoryCommandText } from '@/components/list/listSlashCommands';
import { ChatTableSkeleton } from '@/components/chat/ChatTableCardShell';
import { createDisplayNameFormatter, remarkOutputMentions, renderUserMentions } from '@/components/chat/renderUserMentions';
import type { SourceCardKind, SourceCardScope } from '@/components/source/sourceTableSlice';
import type { TableSlice } from '@/components/tables/analystTableSlice';
import clsx from 'clsx';
import AudioLoadingIndicator from './ui/AudioLoadingIndicator';
import { BrandLogoCarousel } from './portal/BrandLogoCarousel';
import { SourceFocusMark } from './portal/SourceFocusMark';
import { Message, ScriptInstance, ChatStateItem, GenerationTask, UiCommandDefinition, ChatSubjectFocusEntry, ChatTimelineViewMode, MemberData } from '#types/types';
import { COMMAND_DEFINITIONS } from './agentCommands';
import { navigateToAnalystCell } from './tables/tablesStore';
import FaterLogo from './ui/FaterLogo';
import { formatRelativeTimestamp } from '@/components/entities/shortlistCells';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { Check, ChevronDown, ChevronRight, Copy, ArrowDownToLine, ListChecks, RotateCcw } from '@/components/ui/lucide-memo';
import { selectActiveSessionProjectIdFromInventoryState } from '#lib/leasing/chat-session-project-focus';
import { isInertSurface } from '#lib/keyboard-shortcuts';
import { listLiveGenerationTasks, selectChatProjectGenerationTasks } from '#lib/list/listGenerationTelemetry';
import { ListGenerationTelemetry } from '@/components/list/ListGenerationTelemetry';
import { FactoryReferenceResults } from '@/components/list/FactoryReferenceResults';
import { chooseFactoryReference } from '#lib/list/factoryNavigation';
import { URW_ORGANISATION_ID } from '#lib/urwProjectDashboardAccess';
import { PENDING_IMAGE_MARKER } from '#lib/chat/pendingImageMarker';
import { copyTextToClipboard } from '#lib/utils';
import { agentIsWorking } from '#lib/chat/agentIsWorking';
import { isScriptExecuting } from '@/kernel/useCommands';
import { TIMELINE_FEED_LOAD_LIMIT, useTimelineFeedMessages } from './portal/useSessionMessages';
import { UserSelector } from './portal/UserSelector';
import { readCampaignRow } from '#lib/leasing/campaignRow';
import {
  buildSubjectFocusHistoryKey,
  isEntityFocusSubject,
  toSubjectFocusHistoryEntry,
} from '#lib/leasing/session-focus';
import { ACTIVE_SOURCING_CAMPAIGN_STATUSES } from '#lib/leasing/sourcingCampaignLifecycle';
import { startImageEditTurn } from '#lib/chat/imageEditTurn';

interface ChatMessageProps {
  message: Message;
  messageId: string | number;
  onContextMenu: (event: React.MouseEvent<HTMLDivElement>, message: Message, messageKey: string) => void;
  /** Called after each fake-streaming chunk so the parent can scroll the container. */
  onStreamingTick?: () => void;
  inlineTelemetryText?: string | null;
  inlineTelemetryPulsing?: boolean;
  /** List Auto mode: show the version with mentions under the user's own words. */
  technicalPromptOpen?: boolean;
}

/** /compact's own message (its compaction item alone), or the answer of a turn that started by compacting (the item, then the turn). */
const compactionOf = (message: Message | undefined): 'command' | 'turn' | null =>
  message?.role !== 'assistant' || message.designerItems?.[0]?.type !== 'compaction' ? null : message.designerItems.length === 1 ? 'command' : 'turn';
const COMPACTED_LABEL_STYLE: React.CSSProperties = { fontSize: 13 };

const animationSpawnFromLeft = 'spawnInFromLeft 0.35s ease-out';
const animationSpawnFromRight = 'spawnInFromRight 0.35s ease-out';
const DIRECT_PREFIX_RE = /^\[\s*Direct\s*[•·]\s*([^\]]+?)\s*\]\s*/i;

/**
 * A slash command the user sent reads as a command, not as a sentence they typed.
 *
 * Only the `/name` token is coloured, never the whole message: "/refine but keep La Caverne top
 * three" is one command and one instruction, and painting the instruction blue too would say they
 * are the same thing. Unknown slashes stay plain - the curated matchers (Source's menu and List's
 * `/factory`) only match real commands, so a user who types "/foo" sees exactly what they wrote.
 */
const renderUserTextWithSlashCommand = (text: string, onReference?: (folderId: string, imageId?: string) => void): React.ReactNode => {
  if (typeof text !== 'string') return text;
  if (!(isSourceSlashCommandText(text) || isFactoryCommandText(text) || isPlanSlashCommandText(text))) return renderUserMentions(text, onReference);
  const leading = text.length - text.trimStart().length;
  const token = /^\/[a-z-]+/i.exec(text.trimStart())?.[0] ?? '';
  if (!token) return text;
  return (
    <>
      {text.slice(0, leading)}
      <span style={{ color: '#3B82F6' }}>{token}</span>
      {renderUserMentions(text.slice(leading + token.length), onReference)}
    </>
  );
};

const getMessageContentAsString = (message: Message): string => {
  return String(message.content ?? '');
};

const getNormalizedMessageRole = (message: Message): 'user' | 'assistant' | 'system' | 'unknown' => {
  const normalizedRole = String(message.role ?? '').toLowerCase();
  if (normalizedRole === 'user') return 'user';
  if (normalizedRole === 'assistant') return 'assistant';
  if (normalizedRole === 'system') return 'system';
  return 'unknown';
};

const isDirectPromptMessage = (message: Message): boolean => {
  return DIRECT_PREFIX_RE.test(getMessageContentAsString(message));
};

const isUserLikeMessage = (message: Message): boolean => {
  return (
    getNormalizedMessageRole(message) === 'user' ||
    message.agentName === 'direct-mode' ||
    isDirectPromptMessage(message)
  );
};

// The chat image a right-click landed on, for its Download item: the click bubbles up to the
// message's onContextMenu, so the <img> is recovered from the target. Citation favicons live
// inside an <a> and are not downloadable assets.
const contextMenuImageSrc = (target: HTMLElement | null): string => {
  const img = target instanceof HTMLImageElement ? target : (target?.closest?.('img') as HTMLImageElement | null);
  const src = img && !img.closest('a') ? img.currentSrc || img.getAttribute('src') || '' : '';
  return /^(https?:|blob:)/i.test(src) ? src : '';
};

// --- Fake streaming constants (tweak feel here) ---

const STREAM_BASELINE_DELAY = 38;   // ms – base tick between chunks
const STREAM_JITTER_PCT     = 0.45; // ±45 % random jitter on baseline
const STREAM_COMMA_PAUSE    = 60;   // ms – extra pause after , ; :
const STREAM_SENTENCE_PAUSE = 140;  // ms – extra pause after . ! ?
const STREAM_PARAGRAPH_PAUSE = 260; // ms – extra pause after blank line
const STREAM_LINE_PAUSE     = 80;   // ms – extra pause after single \n
// Chunk-size distribution: 70 % → 1-3 words, 30 % → 4-8 words
const STREAM_SMALL_CHUNK_MAX = 3;
const STREAM_LARGE_CHUNK_MAX = 8;
const STREAM_LARGE_CHUNK_CHANCE = 0.30;
const EMPTY_GENERATION_TASKS: GenerationTask[] = [];
const COMMAND_DISPLAY_NAMES = Object.fromEntries(Object.values(COMMAND_DEFINITIONS).map(command => [command.action_type, command.action_type.toLowerCase().replaceAll('_', ' ')]));
const formatActivityText = createDisplayNameFormatter(COMMAND_DISPLAY_NAMES);
const translations = {
  en: {
    loadOlderEvents: 'Load older events',
    loadingOlderEvents: 'Loading older events...',
    copyPrompt: 'Copy prompt',
    copiedPrompt: 'Copied',
  },
  fr: {
    loadOlderEvents: 'Charger les evenements precedents',
    loadingOlderEvents: 'Chargement des evenements precedents...',
    copyPrompt: 'Copier le prompt',
    copiedPrompt: 'Copie',
  },
};

// Clickable Analyst cell citations: the agent writes `[[Sheet!A1]]` (or `[[A1]]`); we turn that into
// a custom-scheme markdown link and render it as a chip that navigates the Analyst to that cell.
const ANALYST_CELL_SCHEME = 'analyst-cell:';
const CITATION_RE = /\[\[([^\]\n]{1,60})\]\]/g;
// Fenced code blocks are split out and left untouched: our table-card markers
// (```fater-analyst-table / ```fater-source-table) carry JSON whose 2D `rows`
// array starts with `[[`, which the citation regex would otherwise mangle into an
// analyst-cell link (breaking a SINGLE-row table's JSON so the card can't parse).
const CODE_FENCE_SPLIT_RE = /(```[\s\S]*?```)/g;
const applyCitations = (text: string): string =>
  text.replace(CITATION_RE, (_m, ref: string) => {
    const r = ref.trim();
    return r ? `[${r}](${ANALYST_CELL_SCHEME}${encodeURIComponent(r)})` : _m;
  });
const preprocessCitations = (content: string): string =>
  content
    .split(CODE_FENCE_SPLIT_RE)
    .map((part) => (part.startsWith('```') ? part : applyCitations(part)))
    .join('');

// While a table-card marker is still streaming in, its fence is not yet closed, so
// react-markdown would render the raw partial JSON as text. Detect an UNCLOSED
// ```fater-source-table / ```fater-analyst-table fence and swap it (to end of string)
// for a loading sentinel that renders a pulsing table skeleton instead of the code.
const TABLE_FENCE_LANGS = ['fater-source-table', 'fater-analyst-table'];
// A message that carries a table-card marker must be left intact end to end: its
// closing ``` fence must survive the trailing-code-block cleanup, and it must NOT be
// fake-streamed char-by-char (that would flash a skeleton / raw JSON).
const contentHasTableMarker = (s: string): boolean => TABLE_FENCE_LANGS.some((l) => s.includes('```' + l));
const preprocessStreamingTables = (content: string, isStreaming: boolean): string => {
  for (const lang of TABLE_FENCE_LANGS) {
    const tag = '```' + lang;
    const open = content.lastIndexOf(tag);
    if (open === -1) continue;
    const nl = content.indexOf('\n', open);
    const close = content.indexOf('```', nl === -1 ? open + tag.length : nl + 1);
    if (close === -1) {
      // Unclosed fence: while streaming this is a partial marker -> show a skeleton;
      // on a finished message the marker is truncated/corrupt -> a note, NEVER an
      // eternal skeleton.
      return (
        content.slice(0, open) +
        (isStreaming ? '```fater-table-loading\n```' : '_This table could not be loaded. Ask me to show it again._')
      );
    }
  }
  return content;
};

// Inline source citation → compact favicon pill (ChatGPT-style). The agent
// cites each claim as a markdown link to the source URL; we render every
// external http(s) link this way so the citation stays attached to its
// statement (favicon + label), instead of bare [n] numbers + a Sources recap.
const InlineSourceCite = ({ href, children }: { href: string; children?: React.ReactNode }) => {
  const domain = domainFromUrl(href);
  const [imgOk, setImgOk] = useState(true);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={domain}
      className="mx-[1px] inline-flex items-center gap-1 rounded-full border border-[#2A2A2A] bg-[#1C1C1C] px-1.5 py-[1px] align-middle text-[0.82em] leading-none text-[#B5B5B5] no-underline transition-colors hover:border-[#3A3A3A] hover:bg-[#222222] hover:text-[#E8E8E8]"
    >
      {imgOk && domain ? (
        <img
          src={googleFaviconUrl(domain, 32)}
          alt=""
          width={12}
          height={12}
          loading="lazy"
          onError={() => setImgOk(false)}
          className="rounded-[2px]"
        />
      ) : null}
      <span className="max-w-[160px] truncate">{children ?? domain}</span>
    </a>
  );
};

// Surface flag for image rendering. The Master surface renders generated images
// as ChatGPT-style cards (rounded 36px + frosted "Edit" pill + download control);
// every other surface keeps the plain rounded inline image. Provided by <Chat> and
// read by the markdown <img> renderer (which is rendered inside the React tree, so
// useContext resolves the nearest provider even though MARKDOWN_COMPONENTS is a
// module-level const).
const ChatImageSurfaceContext = React.createContext<boolean>(false);

// Loading skeleton shown while a Master image generates, then swapped in place for
// the result card. Reuses List Studio's PendingCard treatment (animate-pulse fill +
// sliding indeterminate bar), shaped like the result card (36px radius, target
// aspect). Grays are explicit hex (house rule) rather than low-opacity white.
const ChatImagePendingSkeleton = ({ aspect }: { aspect: number }) => (
  <div
    style={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 36,
      background: '#242424',
      margin: '8px 0',
      width: 'min(100%, 360px)',
      aspectRatio: aspect > 0 ? aspect : 1,
    }}
  >
    <style>{`@keyframes fater-pending-slide{0%{transform:translateX(-120%)}100%{transform:translateX(380%)}}`}</style>
    <div className="animate-pulse" style={{ position: 'absolute', inset: 0, background: '#1F1F1F' }} />
    <div style={{ position: 'absolute', left: 12, right: 12, bottom: 12, height: 4, borderRadius: 1, overflow: 'hidden', background: '#333333' }}>
      <div style={{ height: '100%', width: '33%', borderRadius: 1, background: '#B5B5B5', animation: 'fater-pending-slide 1.15s ease-in-out infinite' }} />
    </div>
  </div>
);

// The ChatGPT image-result card: rounded 36px, overflow-clipped, with a frosted
// "Edit" pill (bottom-left) and a frosted circular download control (bottom-right),
// pixel-matched to ChatGPT (rgba(0,0,0,.15) + backdrop-blur(12px), 40px controls,
// 12px inset). Clicking Edit starts an edit turn in the Master composer.
const ChatMarkdownImage = ({ src, alt }: { src: string; alt?: string }) => {
  const chatGptCard = useContext(ChatImageSurfaceContext);
  const { kernel } = useKernel();
  // Blur-reveal the inline result image: hold a blur until it fully loads, then snap
  // sharp, the same "appear" treatment as the output cards, so a generated image
  // resolves in instead of painting top-to-bottom. Intrinsic-size <img>, so a CSS
  // filter (not the fixed-fill BlurRevealImage).
  const reveal = useBlurRevealFilter(src);
  // Pending-skeleton marker. We use an https sentinel host (not a custom scheme or
  // data: URI) because react-markdown's default urlTransform strips non-http(s)
  // schemes, which would blank the src. It is intercepted here before any <img> is
  // created, so the sentinel URL never triggers a network request.
  const pendingIdx = src.indexOf(PENDING_IMAGE_MARKER);
  if (pendingIdx >= 0) {
    const ratio = parseFloat(src.slice(pendingIdx + PENDING_IMAGE_MARKER.length));
    return <ChatImagePendingSkeleton aspect={Number.isFinite(ratio) && ratio > 0 ? ratio : 1} />;
  }
  if (!chatGptCard) {
    // `reveal.*` is the public surface of useBlurRevealFilter, meant to be spread onto an <img>.
    // The compiler reads it as touching a ref during render; nothing reads `.current` here.
    /* eslint-disable react-hooks/refs */
    return (
      <img
        ref={reveal.ref}
        src={src}
        alt={alt || ''}
        loading="lazy"
        onLoad={reveal.onLoad}
        onError={reveal.onError}
        style={{ maxWidth: '100%', height: 'auto', borderRadius: '20px', display: 'block', margin: '8px 0', ...reveal.style }}
      />
    );
    /* eslint-enable react-hooks/refs */
  }
  const frostedBtn: React.CSSProperties = {
    position: 'absolute',
    bottom: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.15)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    color: '#FFFFFF',
    border: 'none',
    cursor: 'pointer',
  };
  // A SPAN, NOT A DIV, AND THE STYLE IS WHAT MAKES THAT FREE. react-markdown renders an image
  // inside the paragraph it was written in, and a <div> inside a <p> is invalid HTML: the browser
  // closes the paragraph early, so the server tree and the client tree disagree and React reports a
  // hydration error on every assistant message carrying an image. `display: inline-block` gives the
  // same box as before, and <span> with an <img> and two <button>s inside is valid phrasing content.
  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        margin: '8px 0',
        maxWidth: '100%',
        borderRadius: 36,
        overflow: 'hidden',
        lineHeight: 0,
      }}
    >
      {/* The compiler reads `reveal.ref` / `reveal.onLoad` / `reveal.style` as touching a ref during
          render. They are the public surface of useBlurRevealFilter, which hands back exactly this
          shape to spread onto an <img>; there is no render-time read of `.current` here. Long-
          standing pattern, surfaced now only because the preprod merge puts this file in the gate's
          changed set. */}
      {/* eslint-disable react-hooks/refs */}
      <img
        ref={reveal.ref}
        src={src}
        alt={alt || ''}
        loading="lazy"
        onLoad={reveal.onLoad}
        onError={reveal.onError}
        style={{ display: 'block', maxWidth: '100%', height: 'auto', borderRadius: 36, ...reveal.style }}
      />
      {/* eslint-enable react-hooks/refs */}
      <button
        type="button"
        onClick={(event) => { if (kernel.current) startImageEditTurn(kernel.current, event.currentTarget); }}
        style={{ ...frostedBtn, left: 12, height: 40, padding: '0 14px', borderRadius: 9999, fontSize: 14, fontWeight: 500, lineHeight: 1 }}
      >
        Edit
      </button>
      <button
        type="button"
        onClick={() => void kernel.current?.downloadChatImage(src)}
        aria-label="Download image"
        title="Download"
        style={{ ...frostedBtn, right: 12, width: 40, height: 40, borderRadius: 9999 }}
      >
        <ArrowDownToLine size={18} strokeWidth={2} />
      </button>
    </span>
  );
};

// Flatten a react-markdown cell's children to plain text so numeric table cells
// can be right-aligned (financial slices read best with figures flush-right).
const flattenMarkdownText = (node: React.ReactNode): string => {
  if (node === null || node === undefined || node === false || node === true) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(flattenMarkdownText).join('');
  if (typeof node === 'object' && 'props' in (node as { props?: { children?: React.ReactNode } })) {
    return flattenMarkdownText((node as { props?: { children?: React.ReactNode } }).props?.children);
  }
  return '';
};
const isNumericCellText = (node: React.ReactNode): boolean => {
  const t = flattenMarkdownText(node).trim();
  if (!t) return false;
  return /^[(+-]?\s*[€$£]?\s*\d[\d.,\s]*\)?\s*[%x×]?$/.test(t);
};

const NUMERIC_CELL: React.CSSProperties = { textAlign: 'right' };
const fencedCodeText = (children: React.ReactNode): string => Array.isArray(children)
  ? children.map((c) => (typeof c === 'string' ? c : '')).join('')
  : typeof children === 'string'
    ? children
    : '';


const MARKDOWN_COMPONENTS = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
    if (href && href.startsWith(ANALYST_CELL_SCHEME)) {
      const ref = decodeURIComponent(href.slice(ANALYST_CELL_SCHEME.length));
      const bang = ref.indexOf('!');
      const sheet = bang >= 0 ? ref.slice(0, bang) : null;
      const a1 = bang >= 0 ? ref.slice(bang + 1) : ref;
      return (
        <button
          type="button"
          onClick={() => navigateToAnalystCell(sheet, a1)}
          title={`Go to ${ref}`}
          style={{
            display: 'inline',
            padding: '0 4px',
            margin: '0 1px',
            borderRadius: 4,
            border: '1px solid rgba(96,165,250,0.4)',
            background: 'rgba(96,165,250,0.12)',
            color: '#60a5fa',
            font: 'inherit',
            fontSize: '0.92em',
            cursor: 'pointer',
          }}
        >
          {children}
        </button>
      );
    }
    if (href && /^https?:\/\//i.test(href)) {
      return <InlineSourceCite href={href}>{children}</InlineSourceCite>;
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
  // Image outputs in chat (e.g. Master-delivered generations). On the Master
  // surface these render as ChatGPT-style cards (see ChatMarkdownImage); elsewhere
  // as a plain rounded inline image.
  img: ({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) =>
    typeof src === 'string' && src ? <ChatMarkdownImage src={src} alt={alt} /> : null,
  // Tables read as ChatGPT draws them in a reply: no box and no fill, the header set apart by weight and one
  // underline, faint row rules, body-coloured text, the first column flush with the prose. Look in globals.css
  // (.chat-md-table); numeric cells sit flush-right with tabular figures.
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="chat-md-table"><table>{children}</table></div>
  ),
  th: ({ children }: { children?: React.ReactNode }) => <th style={isNumericCellText(children) ? NUMERIC_CELL : undefined}>{children}</th>,
  td: ({ children }: { children?: React.ReactNode }) => <td style={isNumericCellText(children) ? NUMERIC_CELL : undefined}>{children}</td>,
  // A Master chat LIVE table card. ANALYST_SHOW_IN_CHAT emits a fenced
  // ```fater-analyst-table block holding { docId, sheet, range, title }; render it
  // as a ChatGPT-style card bound to the live Analyst doc (ChatAnalystTableCard),
  // unwrapping the default <pre> so the card isn't nested in a code block.
  pre: ({ children }: { children?: React.ReactNode }) => {
    const child = Array.isArray(children) ? children[0] : children;
    const cls = React.isValidElement(child)
      ? (child as React.ReactElement<{ className?: string }>).props?.className
      : undefined;
    if (
      typeof cls === 'string' &&
      (cls.includes('language-fater-analyst-table') ||
        cls.includes('language-fater-source-table') ||
        cls.includes('language-fater-table-loading'))
    ) {
      return <>{children}</>;
    }
    return <pre>{children}</pre>;
  },
  code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
    if (typeof className === 'string' && className.includes('language-fater-table-loading')) {
      return <ChatTableSkeleton />;
    }
    if (typeof className === 'string' && className.includes('language-fater-analyst-table')) {
      const raw = fencedCodeText(children);
      try {
        const parsed = JSON.parse(raw.trim()) as {
          docId?: string;
          sheet?: string;
          range?: string;
          title?: string;
          data?: TableSlice;
        };
        if (parsed && typeof parsed.docId === 'string') {
          return (
            <ChatAnalystTableCard
              docId={parsed.docId}
              sheet={parsed.sheet}
              range={parsed.range}
              title={parsed.title}
              data={parsed.data ?? null}
            />
          );
        }
      } catch {
        /* not a valid card payload, fall through to plain code */
      }
    }
    // SOURCE data card (shortlist / campaigns / contacts / brand). SOURCE_SHOW_IN_CHAT
    // emits a fenced ```fater-source-table block holding { kind, scope, entityType,
    // rowIds, title, data }; render it as a read-only ChatGPT-style table card.
    if (typeof className === 'string' && className.includes('language-fater-source-table')) {
      const raw = fencedCodeText(children);
      try {
        const parsed = JSON.parse(raw.trim()) as {
          kind?: SourceCardKind;
          scope?: SourceCardScope;
          entityType?: string;
          rowIds?: string[];
          columns?: string[];
          total?: number;
          navEntityType?: string;
          navEntityId?: string;
          title?: string;
          data?: TableSlice;
        };
        if (parsed && typeof parsed.kind === 'string') {
          return (
            <ChatSourceTableCard
              kind={parsed.kind}
              scope={parsed.scope}
              entityType={parsed.entityType}
              rowIds={parsed.rowIds}
              columns={parsed.columns}
              total={parsed.total}
              navEntityType={parsed.navEntityType}
              navEntityId={parsed.navEntityId}
              title={parsed.title}
              data={parsed.data ?? null}
            />
          );
        }
      } catch {
        /* not a valid card payload, fall through to plain code */
      }
    }
    return <code className={className}>{children}</code>;
  },
};

const ChatMarkdown = memo(({ content, isStreaming }: { content: string; isStreaming?: boolean }) => {
  const factory = useKernelStore(state => state.inventory.Organisation.registry[URW_ORGANISATION_ID]?.factory);
  const plugins = useMemo(() => {
    const names = { ...COMMAND_DISPLAY_NAMES };
    if (factory?.status === 'ready') for (const folder of factory.referenceFolders) {
      names[folder.id] = folder.name;
      for (const image of folder.images) names[image.id] = image.name;
    }
    return [remarkGfm, [remarkOutputMentions, { names }] as [typeof remarkOutputMentions, { names: Record<string, string> }]];
  }, [factory]);
  return (
    <ReactMarkdown remarkPlugins={plugins} components={MARKDOWN_COMPONENTS}>
      {preprocessCitations(preprocessStreamingTables(content, !!isStreaming))}
    </ReactMarkdown>
  );
});
ChatMarkdown.displayName = 'ChatMarkdown';

type AssistantContentSegment =
  | { type: 'markdown'; content: string }
  | { type: 'modelPrompt'; content: string };

const appendMarkdownSegment = (segments: AssistantContentSegment[], content: string) => {
  const trimmed = content.trim();
  if (!trimmed) return;

  const previous = segments[segments.length - 1];
  if (previous?.type === 'markdown') {
    previous.content = `${previous.content}\n\n${trimmed}`;
    return;
  }

  segments.push({ type: 'markdown', content: trimmed });
};

const appendModelPromptSegment = (
  segments: AssistantContentSegment[],
  content: string,
) => {
  const trimmed = content.trim();
  if (!trimmed) return;
  segments.push({ type: 'modelPrompt', content: trimmed });
};

const PROMPT_LABEL_LINE_RE = /^\s*(?:[-*]\s*)?(?:\*\*)?\s*((?:(?:model|generation|image|video|final|refined|negative|seedance(?:\s*2(?:\.0)?)?|kling)[\w\s().-]*\s+)?prompt(?:\s+for\s+[^:*]+)?)(?:\*\*)?\s*:\s*(.*)$/i;

const collectFencedPromptBody = (lines: string[], bodyStartIndex: number) => {
  const bodyLines: string[] = [];
  let index = bodyStartIndex;

  while (index < lines.length) {
    const line = lines[index] ?? '';
    if (line.trim().startsWith('```')) {
      return { body: bodyLines.join('\n'), endIndex: index };
    }
    bodyLines.push(line);
    index++;
  }

  return { body: bodyLines.join('\n'), endIndex: lines.length - 1 };
};

const splitPromptLabelSegments = (content: string): AssistantContentSegment[] => {
  const segments: AssistantContentSegment[] = [];
  const markdownBuffer: string[] = [];
  const lines = content.split(/\r?\n/);

  const flushMarkdown = () => {
    appendMarkdownSegment(segments, markdownBuffer.join('\n'));
    markdownBuffer.length = 0;
  };

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index] ?? '';
    const labelMatch = line.match(PROMPT_LABEL_LINE_RE);

    if (!labelMatch) {
      markdownBuffer.push(line);
      continue;
    }

    const inlinePrompt = (labelMatch[2] ?? '').trim();

    if (inlinePrompt.startsWith('```')) {
      const fenced = collectFencedPromptBody(lines, index + 1);
      flushMarkdown();
      appendModelPromptSegment(segments, fenced.body);
      index = fenced.endIndex;
      continue;
    }

    if (inlinePrompt) {
      flushMarkdown();
      appendModelPromptSegment(segments, inlinePrompt);
      continue;
    }

    const nextLine = lines[index + 1] ?? '';
    if (nextLine.trim().startsWith('```')) {
      const fenced = collectFencedPromptBody(lines, index + 2);
      flushMarkdown();
      appendModelPromptSegment(segments, fenced.body);
      index = fenced.endIndex;
      continue;
    }

    const promptLines: string[] = [];
    let cursor = index + 1;
    while (cursor < lines.length) {
      const candidate = lines[cursor] ?? '';
      if (!candidate.trim()) break;
      if (PROMPT_LABEL_LINE_RE.test(candidate)) break;
      if (promptLines.length > 0 && /^\s{0,3}#{1,6}\s+/.test(candidate)) break;
      promptLines.push(candidate);
      cursor++;
    }

    if (promptLines.length === 0) {
      markdownBuffer.push(line);
      continue;
    }

    flushMarkdown();
    appendModelPromptSegment(segments, promptLines.join('\n'));
    index = cursor - 1;
  }

  flushMarkdown();
  return segments;
};

const splitAssistantContentSegments = (content: string): AssistantContentSegment[] => {
  const segments: AssistantContentSegment[] = [];
  const promptBlockPattern = /<model_prompt([^>]*)>([\s\S]*?)(?:<\/model_prompt>|$)|```[ \t]*(model[-_\s]?prompt|generation[-_\s]?prompt|image[-_\s]?prompt|video[-_\s]?prompt|negative[-_\s]?prompt|prompt|seedance(?:[-_\s]?2(?:\.0)?)?[-_\s]?prompt)[^\n]*\n([\s\S]*?)(?:```|$)/gi;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = promptBlockPattern.exec(content)) !== null) {
    const before = content.slice(cursor, match.index);
    segments.push(...splitPromptLabelSegments(before));

    if (match[2] !== undefined) {
      appendModelPromptSegment(segments, match[2]);
    } else {
      appendModelPromptSegment(segments, match[4] ?? '');
    }

    cursor = match.index + match[0].length;
    if (match[0].length === 0) {
      promptBlockPattern.lastIndex++;
    }
  }

  segments.push(...splitPromptLabelSegments(content.slice(cursor)));

  if (segments.length === 0 && content.trim()) {
    return [{ type: 'markdown', content: content.trim() }];
  }

  return segments;
};

const ModelPromptNotebook = memo(({ content }: { content: string }) => {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await copyTextToClipboard(content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch (error) {
      console.error('Failed to copy model prompt:', error);
      setCopied(false);
    }
  }, [content]);

  return (
    <div className="my-3 rounded-2xl bg-white/[0.06] px-4 pb-4 pt-2 text-white/90">
      <div className="mb-0.5 flex items-center justify-end">
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? t.copiedPrompt : t.copyPrompt}
          title={copied ? t.copiedPrompt : t.copyPrompt}
          className="-mr-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white/55 transition-colors hover:bg-white/[0.08] hover:text-white/90"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <div className="text-[13px] leading-relaxed text-white/85">
        <ChatMarkdown content={content} />
      </div>
    </div>
  );
});
ModelPromptNotebook.displayName = 'ModelPromptNotebook';

const AssistantMessageContent = memo(({ content, isStreaming }: { content: string; isStreaming?: boolean }) => {
  const allSegments = useMemo(() => splitAssistantContentSegments(content), [content]);

  // LIST projects want the chat to read as simple telemetry: the agent says
  // what it is doing, it does not paper the conversation with the full
  // generation prompt. This covers BOTH sources of that block, the agent's own
  // <model_prompt> in its message, and the "<model> launched on <workspace>"
  // timeline event, which appends the submitted prompt the same way.
  const isListProject = useKernelStore((state) => state.studioViewMode === 'marketing');

  const segments = useMemo(
    () => (isListProject ? allSegments.filter((segment) => segment.type !== 'modelPrompt') : allSegments),
    [allSegments, isListProject],
  );

  return (
    <>
      {segments.map((segment, index) => (
        segment.type === 'modelPrompt'
          ? (
            <ModelPromptNotebook
              key={`prompt-${index}`}
              content={segment.content}
            />
          )
          : <ChatMarkdown key={`markdown-${index}`} content={segment.content} isStreaming={isStreaming} />
      ))}
    </>
  );
});
AssistantMessageContent.displayName = 'AssistantMessageContent';

// --- Fake streaming helpers ---

/**
 * Regex that tokenises text into "atomic" pieces that must never be split:
 *   1. Fenced code blocks:        ```lang\n…\n```
 *   2. Inline code:               `…`
 *   3. Markdown images:           ![alt](url)
 *   4. Markdown links:            [text](url)
 *   5. Bold / italic markers:     ** __ * _  (attached to the next word)
 *   6. Plain words (non-space)
 *   7. Whitespace runs
 */
const TOKEN_RE =
  /```[\s\S]*?```|`[^`]+`|!\[[^\]]*\]\([^)]*\)|\[[^\]]*\]\([^)]*\)|\*{1,2}\S+\*{1,2}|_{1,2}\S+_{1,2}|\S+|\s+/g;

/** Returns a random chunk-size target following the configured distribution. */
function nextChunkSize(): number {
  if (Math.random() < STREAM_LARGE_CHUNK_CHANCE) {
    return STREAM_SMALL_CHUNK_MAX + 1 + Math.floor(Math.random() * (STREAM_LARGE_CHUNK_MAX - STREAM_SMALL_CHUNK_MAX));
  }
  return 1 + Math.floor(Math.random() * STREAM_SMALL_CHUNK_MAX);
}

/** True when a chunk ends at a natural sentence boundary. */
const SENTENCE_END_RE = /[.!?]["')\]]*\s*$/;
const COMMA_END_RE    = /[,;:]["')\]]*\s*$/;

/**
 * Splits `text` into variable-size word-chunks suitable for fake streaming.
 * – Never breaks inside code fences, inline code, links, or formatting tokens.
 * – Prefers to break at spaces/newlines and sentence-ending punctuation.
 */
function buildChunks(text: string): string[] {
  const tokens = text.match(TOKEN_RE) ?? [];
  const chunks: string[] = [];
  let current = '';
  let wordCount = 0;
  let target = nextChunkSize();

  for (const token of tokens) {
    const isWord = /\S/.test(token);
    current += token;
    if (isWord) wordCount++;

    if (wordCount >= target) {
      chunks.push(current);
      current = '';
      wordCount = 0;
      target = nextChunkSize();
    } else if (wordCount > 0 && SENTENCE_END_RE.test(current)) {
      // Prefer to break right after a sentence even if target not reached.
      chunks.push(current);
      current = '';
      wordCount = 0;
      target = nextChunkSize();
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

/** Compute per-chunk delay with jitter and punctuation pauses. */
function chunkDelay(chunk: string): number {
  const jitter = 1 + (Math.random() * 2 - 1) * STREAM_JITTER_PCT; // 0.55 – 1.45
  let delay = STREAM_BASELINE_DELAY * jitter;

  // Punctuation pauses (largest wins when stacked with paragraph/line)
  if (SENTENCE_END_RE.test(chunk))    delay += STREAM_SENTENCE_PAUSE;
  else if (COMMA_END_RE.test(chunk))  delay += STREAM_COMMA_PAUSE;

  if (/\n\n\s*$/.test(chunk))        delay += STREAM_PARAGRAPH_PAUSE;
  else if (/\n\s*$/.test(chunk))      delay += STREAM_LINE_PAUSE;

  return delay;
}

/**
 * Progressively calls onChunk with successive text pieces.
 * Returns a cancel function.
 */
function fakeStream(
  text: string,
  onChunk: (chunk: string) => void,
  onDone: () => void,
): () => void {
  const chunks = buildChunks(text);
  let cancelled = false;
  let i = 0;
  let timeoutId: ReturnType<typeof setTimeout>;

  function scheduleNext() {
    if (cancelled || i >= chunks.length) {
      if (!cancelled) onDone();
      return;
    }
    const chunk = chunks[i]!;
    const delay = chunkDelay(chunk);
    timeoutId = setTimeout(() => {
      onChunk(chunk);
      i++;
      scheduleNext();
    }, delay);
  }

  scheduleNext();
  return () => {
    cancelled = true;
    clearTimeout(timeoutId);
  };
}

const ChatMessage = memo(({
  message,
  messageId,
  onContextMenu,
  onStreamingTick,
  inlineTelemetryText,
  inlineTelemetryPulsing = false,
  technicalPromptOpen = false,
}: ChatMessageProps) => {
  const { kernel } = useKernel();
  const audioEnabled = useKernelStore((state) => state.audioEnabled);
  const currentUserId = useKernelStore((state) => state.userContext.dbUserId);
  // On the Master (ChatGPT-style) surface the message text must match the composer
  // and ChatGPT itself: 16px / 26px, not the app's tiny 13px chat text. The app has
  // a global 12px base and Tailwind text-[16px] classes don't emit, so the size is
  // applied inline below (see bubbleStyle).
  const surfaceIsChatGpt = useContext(ChatImageSurfaceContext);
  const messageContentForDisplay = getMessageContentAsString(message);
  const normalizedRole = getNormalizedMessageRole(message);
  const isUserMessage = isUserLikeMessage(message);
  const isAudioLoading = normalizedRole === 'assistant' && audioEnabled && message.audioUrl === 'waiting';
  const userBubbleRadiusPx = 22;
  const userBubbleRadius = `${userBubbleRadiusPx}px`;

  // Fake streaming state for recent assistant messages.
  // Initialise to '' for recent messages so the very first render is empty (no flash of full text).
  const [displayedContent, setDisplayedContent] = useState<string | null>(() => {
    if (normalizedRole === 'assistant' && message.timestamp) {
      const age = Date.now() - new Date(message.timestamp).getTime();
      if (age < 20_000) return '';
    }
    return null;
  });

  const streamedRef = useRef('');

  const handleImageClick = useCallback((imgIndex: number) => {
    void kernel.current?.reAddChatImageToComposer(message, imgIndex);
  }, [kernel, message]);

  const elementsToRender = [];

    if (isUserMessage && message.imageUrls && message.imageUrls.length > 0) {
    const attachmentRowStyle: React.CSSProperties = {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
      animation: animationSpawnFromRight,
    };

    const attachmentWrapperStyle: React.CSSProperties = {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      position: 'relative',
      padding: '4px',
    };

    elementsToRender.push(
      <div
        key={`${messageId}-images`}
        style={attachmentRowStyle}
      >
        <div
          style={attachmentWrapperStyle}
          onContextMenu={(e) => onContextMenu(e, message, String(messageId))}
        >
              {message.imageUrls.map((url: string, imgIndex: number) => (
                <div
                  key={imgIndex}
                  className="chat-message-attachment-wrap"
                  style={{
                    width: '120px',
                    height: '120px',
                    position: 'relative',
                    borderRadius: userBubbleRadius,
                    ['--chat-user-bubble-radius' as any]: userBubbleRadius,
                    overflow: 'hidden',
                    clipPath: `inset(0 round ${userBubbleRadius})`,
                    cursor: 'pointer',
                  }}
                  onClick={() => handleImageClick(imgIndex)}
                >
                  <BlurRevealImage
                    src={url}
                    alt={`Image ${imgIndex + 1}`}
                    loading="lazy"
                    fetchPriority="low"
                    // Blur-reveal the attached reference thumbnail on load, like the
                    // output cards. The 120x120 wrapper is already a fixed-size,
                    // overflow-hidden positioning context, so the fill variant fits.
                    className="absolute inset-0 h-full w-full"
                    imgClassName="object-cover"
                  />
                </div>
              ))}
        </div>
      </div>
    );
  }

  const directPromptMeta = useMemo(() => {
    if (!isUserMessage || !messageContentForDisplay) {
      return null;
    }

    const directPrefixMatch = messageContentForDisplay.match(DIRECT_PREFIX_RE);
    if (!directPrefixMatch) {
      return null;
    }

    const rawModelLabel = directPrefixMatch[1]?.trim() || '';
    const strippedPrompt = messageContentForDisplay.slice(directPrefixMatch[0].length).trimStart();

    return {
      subtext: rawModelLabel ? `Direct • ${rawModelLabel}` : 'Direct',
      promptContent: strippedPrompt,
    };
  }, [isUserMessage, messageContentForDisplay]);

  const userMessageContentForDisplay = useMemo(() => {
    if (!isUserMessage) {
      return messageContentForDisplay;
    }
    return directPromptMeta?.promptContent ?? messageContentForDisplay;
  }, [isUserMessage, directPromptMeta, messageContentForDisplay]);

  const cleanedContent = useMemo(() => {
    if (!messageContentForDisplay) {
      return '';
    }

    // A table-card marker must keep its closing ``` fence: the trailing-code-block
    // cleanup below would otherwise strip it, leaving an unclosed fence that renders as
    // an endless skeleton. Leave marker messages untouched.
    if (contentHasTableMarker(messageContentForDisplay)) {
      return messageContentForDisplay.trim();
    }

    // Remove trailing fenced code blocks like ```json ... ```
    const withoutTrailingCodeBlock = messageContentForDisplay.replace(
      /```[a-zA-Z0-9_-]*\s*$/g,
      '',
    );

    return withoutTrailingCodeBlock
      .replace(/```json\s*```/g, '')
      .replace(/```\s*```/g, '')
      .trim();
  }, [messageContentForDisplay]);

  // Trigger fake streaming for recent assistant messages: a delta that extends what is shown continues from it.
  // Cleanup cancels the previous stream before its first chunk lands, so StrictMode's second invoke is harmless.
  useEffect(() => {
    if (normalizedRole !== 'assistant' || !cleanedContent) {
      return;
    }
    // Messages older than 20 s were loaded from history, show them immediately. Also
    // reveal a table-card marker instantly: fake-streaming its JSON char-by-char would
    // just flash a skeleton until the closing fence is revealed.
    const ageMs = message.timestamp ? Date.now() - new Date(message.timestamp).getTime() : Infinity;
    if (ageMs >= 20_000 || contentHasTableMarker(cleanedContent)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing when this state settles is a behavioural change, not merge work
      setDisplayedContent(cleanedContent);
      return;
    }

    // A real stream changes the content on every delta: continue from what is already shown,
    // so the paragraphs on screen keep their nodes instead of being emptied and rebuilt.
    const shown = cleanedContent.startsWith(streamedRef.current) ? streamedRef.current : '';
    streamedRef.current = shown;
    setDisplayedContent(shown);
    // Capture ref so the closure isn't stale if prop identity changes.
    const tick = onStreamingTick;
    let rafId = 0;
    const cancel = fakeStream(
      cleanedContent.slice(shown.length),
      (chunk) => {
        streamedRef.current += chunk;
        setDisplayedContent(prev => (prev ?? '') + chunk);
        // Schedule scroll after React commits the new text height.
        if (tick) {
          cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(tick);
        }
      },
      () => { cancelAnimationFrame(rafId); },
    );
    return () => { cancel(); cancelAnimationFrame(rafId); };
  }, [message.timestamp, cleanedContent, normalizedRole]);

  // The content to actually render, displayedContent while streaming, full text otherwise.
  const renderedContent = (normalizedRole === 'assistant' && displayedContent !== null)
    ? displayedContent
    : cleanedContent;

  const hasTextContent = isUserMessage
    ? userMessageContentForDisplay.trim() !== ''
    : cleanedContent !== '';
  const timelineEventId = typeof message.timelineEventId === 'string' ? message.timelineEventId.trim() : '';
  const normalizedCurrentUserId = typeof currentUserId === 'string' ? currentUserId.trim() : '';
  const authorUserId = useKernelStore((state) => {
    if (!timelineEventId) return '';
    const timelineEvent = state.inventory.TimelineEvent.registry[timelineEventId];
    const candidate = typeof timelineEvent?.userId === 'string' ? timelineEvent.userId.trim() : '';
    return candidate;
  });
  const foreignUserRecord = useKernelStore((state) => {
    if (!authorUserId || !normalizedCurrentUserId || authorUserId === normalizedCurrentUserId) {
      return null;
    }
    return state.inventory.User.registry[authorUserId] ?? null;
  });
  const foreignUserBadge = useMemo<MemberData | null>(() => {
    if (!isUserMessage) return null;
    if (!authorUserId || !normalizedCurrentUserId || authorUserId === normalizedCurrentUserId) return null;
    if (foreignUserRecord) return foreignUserRecord as MemberData;
    const fallbackSuffix = authorUserId.slice(0, 6);
    return {
      id: authorUserId,
      organisationId: null,
      firstName: 'User',
      lastName: fallbackSuffix,
      email: `user-${fallbackSuffix}@fater.local`,
      imageUrl: null,
      isOrganisationAdmin: false,
      lastActive: null,
      projectCount: 0,
      createdAt:
        typeof message.timestamp === 'string' && message.timestamp.trim().length > 0
          ? message.timestamp
          : new Date(0).toISOString(),
      generationCount: 0,
    };
  }, [authorUserId, foreignUserRecord, isUserMessage, message.timestamp, normalizedCurrentUserId]);

  const isAssistantOrSystem = normalizedRole === 'assistant' || normalizedRole === 'system';
  // AN ASSISTANT MESSAGE WITH NOTHING IN IT IS NOT RENDERED. The empty bubble still carried its
  // 8px top and bottom padding, so a turn that only ran commands laid a 16px invisible block into
  // the stack, and the two command rows around it ended up 32px apart where two paragraphs are 8.
  // That is the uneven spacing that was reported: the flex gap was uniform all along, measured at
  // exactly 8px between every entry on 2026-08-16, so tightening it would have moved everything and
  // fixed nothing. The empty message is dropped, not restyled, because it says nothing to anyone.
  // The two things such a message can still legitimately carry keep it alive.
  const carriesSomethingVisible = hasTextContent
    || (isAssistantOrSystem && (isAudioLoading || Boolean(inlineTelemetryText)));

  if ((isUserMessage || isAssistantOrSystem) && carriesSomethingVisible) {

    const messageRowStyle: React.CSSProperties = {
      position: 'relative',
      display: 'flex',
      justifyContent: isUserMessage ? 'flex-end' : 'flex-start',
      alignItems: 'flex-start',
      animation: isUserMessage ? animationSpawnFromRight : animationSpawnFromLeft,
      marginBottom: '0',
    };

    const wrapperStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      // The hidden user action row (copy / resend / timestamp) can be wider than
      // a short bubble. Keep the bubble pinned to that row's right edge instead
      // of letting it start at the row's left edge and leave a phantom gutter.
      alignItems: isUserMessage ? 'flex-end' : 'stretch',
      width: isUserMessage ? 'fit-content' : '100%',
      maxWidth: '100%',
      marginLeft: isUserMessage ? 'auto' : undefined,
      wordBreak: 'break-word',
    };
    const bubbleStyle: React.CSSProperties = {
      position: 'relative',
      background: isUserMessage
        ? '#232325'
        : 'transparent',
      color: '#FAF9F5',
      padding: isUserMessage ? (surfaceIsChatGpt ? '10px 16px' : '8px 14px') : '8px 0',
      borderRadius: isUserMessage ? userBubbleRadius : '0',
      border: 'none',
      boxShadow: 'none',
      fontSize: surfaceIsChatGpt ? '16px' : '13px',
      lineHeight: surfaceIsChatGpt ? '26px' : '1.45',
      width: isUserMessage ? 'fit-content' : '100%',
      maxWidth: '100%',
      wordBreak: 'break-word',
      cursor: 'context-menu',
      fontFamily: isUserMessage
        ? TRACE_FONT
        : undefined,
    };

    const directSubtextStyle: React.CSSProperties = {
      marginTop: '6px',
      color: '#8d8d93',
      fontSize: surfaceIsChatGpt ? '16px' : '11px',
      lineHeight: surfaceIsChatGpt ? '26px' : '1.25',
      fontFamily: TRACE_FONT,
      textAlign: 'right',
    };
    const inlineTelemetryStyle: React.CSSProperties = {
      marginTop: '6px',
      color: inlineTelemetryPulsing ? 'rgba(255,255,255,0.30)' : 'rgba(255,255,255,0.24)',
      fontSize: surfaceIsChatGpt ? '16px' : '11px',
      lineHeight: surfaceIsChatGpt ? '26px' : '1.2',
      fontFamily: TRACE_FONT,
      textAlign: 'left',
      letterSpacing: '0.01em',
    };

    elementsToRender.push(
      <div
        key={isUserMessage && message.imageUrls && message.imageUrls.length > 0 ? `${messageId}-text` : messageId}
        style={messageRowStyle}
      >
        <div style={wrapperStyle} className="group/msg">
          {isUserMessage && foreignUserBadge && (
            <div className="mb-1 flex justify-end">
              <UserSelector
                selectedMember={foreignUserBadge}
                disabled
                showName
              />
            </div>
          )}
          <div
            style={bubbleStyle}
            onContextMenu={(e) => onContextMenu(e, message, String(messageId))}
            className="chat-message-content-area"
          >
            {isAssistantOrSystem
              ? <AssistantMessageContent content={renderedContent} isStreaming={displayedContent !== null} />
              : (
                // The user text sits in a plain <div>, and the app's global
                // `div { font-size: 12px }` base overrides the 16px inherited from
                // the bubble wrapper. Inline font-size beats that rule, so on the
                // Master surface force the div to inherit the bubble's 16px / 26px.
                <div style={surfaceIsChatGpt ? { fontSize: 'inherit', lineHeight: 'inherit' } : undefined}>
                  {renderUserTextWithSlashCommand(userMessageContentForDisplay, (folderId, imageId) => {
                    const k = kernel.current;
                    const sessionId = k?.getState().activeChatSessionId;
                    if (k && sessionId) void chooseFactoryReference(k, sessionId, folderId, imageId).catch(error => k.handleError(error));
                  })}
                </div>
              )
            }
            {isAssistantOrSystem && isAudioLoading && <AudioLoadingIndicator />}
          </div>

          {isUserMessage && (
            /* Hover actions (SRC-031, sized down SRC-033): copy, structural resend, when it was sent.
               Claude-chat sized: 11px ghost icons, 10px muted time, hairline gaps. The resend goes
               through submitChatPrompt with promptOverride - the real send pipeline, a brand-new
               user turn with the same text - never a paste into the composer. */
            <div className="mt-0.5 flex items-center justify-end gap-0.5 opacity-0 transition-opacity duration-150 group-hover/msg:opacity-100">
              <button
                type="button"
                title="Copy"
                aria-label="Copy message"
                onClick={() => { void copyTextToClipboard(messageContentForDisplay); }}
                className="rounded-[4px] p-[3px] text-[#6E6E72] transition-colors hover:bg-white/[0.07] hover:text-[#C9C9CE]"
              >
                <Copy size={11} strokeWidth={1.8} />
              </button>
              <button
                type="button"
                title="Resend"
                aria-label="Resend message"
                data-resend-message="1"
                onClick={() => { void kernel.current?.submitChatPrompt({ promptOverride: messageContentForDisplay, imageUrlOverrides: message.imageUrls }); }}
                className="rounded-[4px] p-[3px] text-[#6E6E72] transition-colors hover:bg-white/[0.07] hover:text-[#C9C9CE]"
              >
                <RotateCcw size={11} strokeWidth={1.8} />
              </button>
              {message.timestamp && (
                <span className="ml-1" style={{ fontSize: 10, lineHeight: '10px', color: '#6E6E72' }}>
                  {formatRelativeTimestamp(String(message.timestamp), 'en')}
                </span>
              )}
            </div>
          )}
          {isUserMessage && directPromptMeta?.subtext && (
            <div style={directSubtextStyle}>
              {directPromptMeta.subtext}
            </div>
          )}

          {isAssistantOrSystem && inlineTelemetryText && (
            <div style={inlineTelemetryStyle}>
              <span className={inlineTelemetryPulsing ? 'thinking-text-animated' : undefined} style={{ fontSize: 'inherit' }}>
                {formatActivityText(inlineTelemetryText)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  if (technicalPromptOpen && message.technicalPrompt) {
    elementsToRender.push(
      <div key={`${messageId}-technical-prompt`} className="flex justify-end">
        <div aria-label="Technical prompt" className="max-w-[85%] whitespace-pre-wrap break-words rounded-lg border border-white/10 px-3 py-2 font-mono text-white/55" style={{ fontSize: 11 }}>
          {message.technicalPrompt}
        </div>
      </div>
    );
  }
  return <>{elementsToRender}</>;
});
ChatMessage.displayName = 'ChatMessage';

// ─── Command Trace (reads directly from scriptsInstances on the message) ─────
const TRACE_FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Segoe UI", Helvetica, Arial, sans-serif';

const IRREGULAR_PAST: Record<string, string> = {
  setting: 'set', getting: 'got', running: 'ran', making: 'made',
  taking: 'took', giving: 'gave', finding: 'found', sending: 'sent',
  building: 'built', keeping: 'kept', thinking: 'thought', bringing: 'brought',
  writing: 'wrote', reading: 'read', knowing: 'knew', going: 'went',
  doing: 'did', having: 'had', being: 'was', meeting: 'met',
  leaving: 'left', leading: 'led', holding: 'held', losing: 'lost',
  paying: 'paid', feeling: 'felt', telling: 'told', beginning: 'began',
  putting: 'put', cutting: 'cut', letting: 'let', hitting: 'hit',
  sitting: 'sat', winning: 'won', spending: 'spent', choosing: 'chose',
  breaking: 'broke', seeing: 'saw', buying: 'bought', falling: 'fell',
  showing: 'showed', growing: 'grew', drawing: 'drew', speaking: 'spoke',
  driving: 'drove', standing: 'stood', understanding: 'understood',
};

/** Convert "Fetching the data" → "Fetched the data" */
const toPastTense = (text: string): string =>
  text.replace(/^(\S+)ing\b/, (match, base: string) => {
    const lower = match.toLowerCase();
    const irr = IRREGULAR_PAST[lower];
    if (irr) return match[0] === match[0]?.toUpperCase() ? irr.charAt(0).toUpperCase() + irr.slice(1) : irr;
    return base + 'ed';
  });

const isWebSearchCommand = (c: { action_type: string }): boolean =>
  c.action_type === 'WEB_SEARCH' || c.action_type === 'SLIDES_WEB_SEARCH';

// Build a WebBrowsingState from the WEB_SEARCH commands in a batch of scripts.
// Reads each command's persisted `parameters.results` (the normalized Exa/LangSearch
// payload the handler stored, the same data that feeds autoRecall), so the
// Claude-grade browsing UX survives the turn lifecycle instead of relying on a
// transient message field. Phase is derived from live command status.
function buildWebBrowsingFromScripts(scripts: ScriptInstance[], runningScriptId: string | null): WebBrowsingState | null {
  const hasWeb = scripts.some(s => s.commands.some(isWebSearchCommand));
  if (!hasWeb) return null;
  const queries: string[] = [];
  const sources: WebSource[] = [];
  const seen = new Set<string>();
  let anyRunning = false;
  for (const s of scripts) {
    const scriptIsRunning = Boolean(runningScriptId) && s.id === runningScriptId;
    for (const c of s.commands) {
      if (!isWebSearchCommand(c)) continue;
      const q = c.parameters?.query;
      if (typeof q === 'string' && q.trim()) queries.push(q.trim());
      const raw = c.parameters?.results as any;
      const arr = Array.isArray(raw?.results)
        ? raw.results
        : (raw?.data?.webPages?.value ?? raw?.webPages?.value ?? (Array.isArray(raw?.data) ? raw.data : []));
      const hasResults = Array.isArray(arr) && arr.length > 0;
      // Shimmer ONLY while the actively-running script is still fetching (no
      // results yet). Never for a completed turn, a historical/persisted search,
      // or a WEB_SEARCH command whose status was left stuck at 'running'.
      if (scriptIsRunning && !hasResults) anyRunning = true;
      if (!Array.isArray(arr)) continue;
      for (const p of arr) {
        const url = typeof p?.url === 'string' ? p.url : '';
        if (!url || seen.has(url)) continue;
        seen.add(url);
        sources.push({
          url,
          title: typeof p?.title === 'string' ? p.title : (typeof p?.name === 'string' ? p.name : undefined),
          snippet: typeof p?.snippet === 'string' ? p.snippet : (typeof p?.summary === 'string' ? p.summary : undefined),
          imageUrl: typeof p?.image === 'string' ? p.image : undefined,
        });
      }
    }
  }
  // A COMMAND THAT NEVER RAN HAS NO OUTCOME TO REPORT. The script persisted on a message is the
  // copy parsed from the stream, BEFORE the runner touched it: its commands sit at 'pending' with
  // no results, forever. Rendering that as "3 searches, no sources found" states an outcome that
  // never happened - measured on 2026-08-16, where the same query answered with a real source when
  // the endpoint was called directly. Unexecuted is not empty, and the line now says so.
  const neverRan = queries.length > 0
    && sources.length === 0
    && scripts.every((s) => s.commands.every((c) => !isWebSearchCommand(c) || c.status === 'pending'));
  const phase: WebBrowsingPhase = anyRunning ? 'searching' : 'done';
  return { phase, queries, sources: sources.slice(0, 12), neverRan };
}

const CommandTrace = memo(({ scripts, runningScriptId, isLast = false, stopRequested = false }: {
  scripts: ScriptInstance[];
  runningScriptId: string | null;
  isLast?: boolean;
  stopRequested?: boolean;
}) => {
  // TELEMETRY IS ONE QUIET LINE, the same as the web-search receipt beside it (WebBrowsingSegment):
  // a 14px glyph, 14px #8E8E8E text on one line, the chevron after the words as ChatGPT's "Thought
  // for 6s >" (owner, 2026-09-29: the 16px wrapping line with a leading chevron was not ChatGPT, and
  // 12px read too small). Sizes are inline: the app's global clamp beats Tailwind text-[Npx].
  const traceFontSize = 14;
  // WEB_SEARCH commands render as the richer WebBrowsingSegment above this trace,
  // so exclude them here to avoid a duplicate "Searched N queries" line.
  // A step still marked running in a script that is no longer the live one was cut off by Stop: it
  // reads as the runner's own stopped step rather than pulsing under a finished trace.
  const commands = scripts
    .flatMap(s => s.id === runningScriptId ? s.commands : s.commands.map(c => c.status === 'running' ? { ...c, status: 'failed' as const, error: 'Stopped' } : c))
    .filter(c => !isWebSearchCommand(c));


  // THE LIVE COUNT OF THE WORK THIS TRACE SET OFF, when that work outlives the command that started
  // it. Read as a string, deliberately: a selector returning a fresh object re-renders on every
  // store tick, and this one is subscribed to a registry that moves constantly during a run.
  const liveRunProgress = useKernelStore((state) => {
    for (const cmd of commands) {
      const def = (COMMAND_DEFINITIONS as Record<string, UiCommandDefinition | undefined>)[cmd.action_type];
      const unit = def?.traceLabels?.progressUnit;
      if (!unit) continue;
      const params = (cmd.parameters ?? {}) as {
        campaignId?: unknown;
        result?: { campaignId?: unknown; brandsRequested?: unknown };
      };
      // The handler writes the campaign it acted on into its own result, which is the only place it
      // is known when the agent let `campaignId` default to whatever was open.
      const campaignId = typeof params.result?.campaignId === 'string'
        ? params.result.campaignId
        : typeof params.campaignId === 'string' ? params.campaignId : '';
      if (!campaignId) continue;
      const campaign = readCampaignRow(state.inventory, campaignId) as
        { status?: unknown; metrics?: { progress?: { completed?: unknown; total?: unknown } } } | undefined;
      // A QUEUED RUN IS STILL THIS COMMAND'S WORK. The campaign publishes no progress until the
      // worker picks it up, which took 75 seconds when this was measured, and for all of it the
      // static label said "Extended 1 shortlist" - a finished sentence about work that had not
      // started. The count the user asked for is known from the moment the command returns, so it
      // stands in until the run reports its own.
      const requested = typeof params.result?.brandsRequested === 'number' ? params.result.brandsRequested : 0;
      const progress = campaign?.metrics?.progress;
      const completed = typeof progress?.completed === 'number' ? progress.completed : 0;
      const total = typeof progress?.total === 'number' && progress.total > 0 ? progress.total : requested;
      // Live only while the run is alive. A finished run reports completed === total and a dead one
      // leaves a terminal status; either way the static label is the better sentence for history,
      // and this is what stops a run that never started counting forever.
      const isActive = (ACTIVE_SOURCING_CAMPAIGN_STATUSES as readonly string[]).includes(String(campaign?.status ?? '').toUpperCase());
      if (total > 0 && completed < total && isActive) return `${cmd.action_type}|${completed}|${total}|${unit}`;
    }
    return '';
  });

  const hasRunningCommand = !stopRequested && commands.some(c => c.status === 'running');
  const allDone = commands.every(c => c.status === 'success' || c.status === 'failed');
  // Last trace starts expanded, previous ones start collapsed
  const [collapsed, setCollapsed] = useState(!isLast);
  const [showAll, setShowAll] = useState(false);

  // Auto-collapse when this trace's commands finish AND it's no longer the last
  const prevAllDoneRef = useRef(allDone);
  const prevIsLastRef = useRef(isLast);
  useEffect(() => {
    if (allDone && !prevAllDoneRef.current) setCollapsed(true);
    if (!isLast && prevIsLastRef.current) setCollapsed(true);
    prevAllDoneRef.current = allDone;
    prevIsLastRef.current = isLast;
  }, [allDone, isLast]);

  const summary = useMemo(() => {
    const running = hasRunningCommand;
    const counts: Record<string, number> = {};
    // ONLY A STEP THAT SUCCEEDED MAY BE REPORTED AS DONE. This counted every command by
    // action_type alone, whatever became of it, so two commands the runner had REFUSED still read
    // "Configured 1 strategy, focused 1 view": the exact line under the 2026-08-15 sourcing report,
    // where nothing was configured, nothing was focused, and the database holds no write for
    // either. The trace is the only account of what the agent did, so three investigations opened
    // from that line believing one had landed.
    //
    // While the script is still LIVE a pending command is work still coming, and the active tense
    // is about to be true of it, so it keeps counting. Once the script is over, pending means it
    // never ran (the runner breaks out of the loop on a blocking failure) and it counts for nothing.
    let failed = 0;
    let stopped = 0;
    for (const cmd of commands) {
      if (cmd.status === 'failed') {
        if (cmd.error === 'Stopped') stopped += 1;
        else failed += 1;
        continue;
      }
      // EVERYTHING THAT DID NOT FAIL COUNTS, and the failures already left this loop above.
      //
      // There used to be a second guard here, `if (!running && cmd.status !== 'success') continue`,
      // and it was too strict: the runner sets success on its own command object and the rendered
      // snapshot does not always carry it yet, so commands that had run and returned results counted
      // for nothing. The group then had nothing to name itself with and printed the bare word
      // "Done", three times in a row on 2026-08-16 over steps that had fetched a campaign's leads
      // and read two brand profiles.
      //
      // The guarantee it was there to protect is intact without it: a REFUSED command is marked
      // 'failed' by the runner and is skipped at the top, so a trace of refused steps still cannot
      // read as a trace of work done. That is what the compiler was pointing out when it called this
      // comparison impossible.
      // A STEP THAT NEVER RAN COUNTS FOR NOTHING. The runner breaks out of its loop on an unknown
      // command, a headless-incompatible one, or a hand-off, leaving the rest at 'pending' forever.
      // The old guard here also excluded anything not yet stamped 'success', which was too strict -
      // the runner sets success on its own object and the rendered snapshot lags, so real work
      // printed the bare word "Done". Excluding ONLY 'pending', and only once the script is over,
      // keeps both guarantees: a 5-step script that dies at 3 says so instead of claiming 4 queries.
      if (!running && cmd.status === 'pending') continue;
      counts[cmd.action_type] = (counts[cmd.action_type] || 0) + 1;
    }

    // Read traceLabels off each command definition. Keeping this here
    // (rather than a hard-coded switch in this render file) means each
    // command's tense/unit lives next to the `action_type` where it was
    // declared, new commands get trace labels by setting `traceLabels`
    // on the definition, no edits to Chat.tsx required.
    const parts: string[] = [];
    let uncategorised = 0;
    for (const [actionType, n] of Object.entries(counts)) {
      if (n === 0) continue;
      const def = (COMMAND_DEFINITIONS as Record<string, UiCommandDefinition | undefined>)[actionType];
      const labels = def?.traceLabels;
      if (!labels) {
        uncategorised += n;
        continue;
      }
      const { active, done, unit, pluralUnit } = labels;
      const [progressAction, progressDone, progressTotal, progressUnit] = liveRunProgress.split('|');
      if (progressAction === actionType && progressUnit) {
        // "extending 3 of 5 brands", counting up, for as long as the run is alive.
        parts.push(`${active} ${progressDone} of ${progressTotal} ${progressUnit}${progressTotal === '1' ? '' : 's'}`);
        continue;
      }
      const u = n !== 1 ? (pluralUnit ?? unit + 's') : unit;
      parts.push(`${running ? active : done} ${n} ${u}`);
    }

    // Catch-all for commands whose definition doesn't carry traceLabels
    // (Studio/Design commands, external or future commands).
    if (uncategorised > 0) {
      parts.push(`${running ? 'running' : 'ran'} ${uncategorised} command${uncategorised !== 1 ? 's' : ''}`);
    }

    // Named last and named plainly, so a trace whose every step was refused cannot read as a trace
    // of work done. Deliberately "failed" and not "did not run": the same status also covers a
    // command stopped or thrown AFTER its handler wrote, and claiming that one never ran would be
    // the same lie pointing the other way. The reason for each is on its own line below.
    if (failed > 0) {
      parts.push(`${failed} step${failed !== 1 ? 's' : ''} failed`);
    }
    if (stopped > 0) parts.push('stopped');

    // Reaching here means every step failed, or the group was empty. Say how many steps rather than
    // the bare word "done", which summarises nothing. The named labels above are the normal path and
    // read "Fetched 10 leads", "Queried 1 entity": each command's tense and unit live on its own
    // definition, so a group is described by what it did, never by a word invented here.
    if (parts.length === 0) {
      parts.push(running ? 'running...' : `${commands.length} step${commands.length !== 1 ? 's' : ''}`);
    }
    const joined = parts.join(', ');
    return joined.charAt(0).toUpperCase() + joined.slice(1);
  }, [commands, hasRunningCommand, liveRunProgress]);

  if (commands.length === 0) return null;

  return (
    <div
      style={{ fontFamily: TRACE_FONT, animation: 'fadeIn 0.8s ease-out' }}
    >
      <button
        type="button"
        onClick={() => setCollapsed(prev => !prev)}
        aria-expanded={!collapsed}
        className="my-1 flex max-w-full items-center gap-2 text-left text-[#8E8E8E] transition-colors hover:text-[#ECECEC]"
        style={{ fontSize: traceFontSize, lineHeight: '20px' }}
      >
        <ListChecks size={14} strokeWidth={2} className="shrink-0" aria-hidden />
        <span className={clsx('truncate', hasRunningCommand && collapsed && 'thinking-text-animated')} style={{ fontSize: 'inherit' }}>{summary}</span>
        <ChevronRight
          size={14}
          aria-hidden
          className={clsx('shrink-0 transition-transform duration-150', !collapsed && 'rotate-90')}
        />
      </button>

      {!collapsed && (() => {
        const maxVisible = 10;
        const visible = showAll ? commands : commands.slice(0, maxVisible);
        const hiddenCount = showAll ? 0 : commands.length - maxVisible;
        const itemCount = visible.length + (hiddenCount > 0 ? 1 : 0);
        return (
          <div className="mb-1 ml-[7px] pl-[15px]">
            {visible.map((cmd, i) => {
              const isLast = i === itemCount - 1;
              return (
              <div
                key={cmd.id}
                className="relative py-[4px]"
                style={{ animation: `fadeIn 0.6s ease-out ${i * 0.1}s both` }}
              >
                {/* Vertical line: full height except last item stops at dash */}
                <div
                  className="absolute left-[-15px] top-0 w-px bg-[#2F2F2F]"
                  style={isLast ? { height: '13px' } : { bottom: 0 }}
                />
                <div className="absolute left-[-15px] top-[13px] w-[7px] border-b border-[#2F2F2F]" />
                <div
                  className={clsx(
                    'text-[#8E8E8E]',
                    cmd.status === 'running' && !stopRequested && 'thinking-text-animated',
                  )}
                  style={{ fontSize: traceFontSize, lineHeight: '20px' }}
                >
                  {/* Past tense is a claim that it happened, so only a step that succeeded earns
                      it. A refused one keeps the wording of the plan it never became, and says why
                      on the line below. */}
                  {cmd.status === 'success'
                    ? toPastTense(formatActivityText(cmd.description || cmd.action_type))
                    : formatActivityText(cmd.description || cmd.action_type)}
                </div>
                {/* A refusal is written for whoever is looking at this trace and is read nowhere
                    else: a refused command marks the script failed, which suppresses the follow-up
                    turn, so nothing paraphrases it back. Clipped, because the same field also
                    carries raw provider and database exceptions, written for the agent's own
                    recovery prompt rather than for a person. */}
                {(cmd.resultSummary || (cmd.status === 'failed' && cmd.error)) && (
                  <div className="mt-[1px] text-[#6E6E6E]" style={{ fontSize: 13, lineHeight: '18px' }}>
                    {formatActivityText(cmd.resultSummary || String(cmd.error).slice(0, 200))}
                  </div>
                )}
              </div>
              );
            })}
            {hiddenCount > 0 && (
              <div className="relative py-[4px]">
                <div
                  className="absolute left-[-15px] top-0 w-px bg-[#2F2F2F]"
                  style={{ height: '13px' }}
                />
                <div className="absolute left-[-15px] top-[13px] w-[7px] border-b border-[#2F2F2F]" />
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="text-left text-[#8E8E8E] transition-colors hover:text-[#ECECEC]"
                  style={{ fontSize: traceFontSize, lineHeight: '20px' }}
                >
                  Show {hiddenCount} more
                </button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
});
CommandTrace.displayName = 'CommandTrace';

// ChatGPT-style right-side scroll rail: a fixed column of short rounded ticks
// (one per conversation turn), vertically centered at the viewport's right edge.
// The active turn's tick is wider + brighter; clicking a tick scrolls that turn
// into view. Only mounted on the Master surface. Anchors are the [data-chat-turn]
// user-message wrappers, re-measured on content/size changes (rAF-coalesced).
// A side chat is too narrow for a rail beside its turns; only a full-width conversation gets one.
const RAIL_MIN_CHAT_WIDTH = 720;
const ChatScrollRail = ({ scrollRef }: { scrollRef: React.RefObject<HTMLDivElement | null> }) => {
  const [ticks, setTicks] = useState<{ top: number; label: string }[]>([]);
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState<number | null>(null);
  const [railHovered, setRailHovered] = useState(false);
  const [scrollable, setScrollable] = useState(false);
  const [wide, setWide] = useState(false);
  const topsRef = useRef<number[]>([]);
  const rafRef = useRef<number | null>(null);

  const updateActive = useCallback(() => {
    const c = scrollRef.current;
    const tops = topsRef.current;
    if (!c || tops.length === 0) return;
    const probe = c.scrollTop + c.clientHeight * 0.3;
    let idx = 0;
    for (let i = 0; i < tops.length; i++) {
      if (tops[i]! <= probe) idx = i;
    }
    if (c.scrollHeight - c.scrollTop - c.clientHeight < 8) idx = tops.length - 1;
    setActive(idx);
  }, [scrollRef]);

  const measure = useCallback(() => {
    const c = scrollRef.current;
    if (!c) return;
    const anchors = Array.from(c.querySelectorAll<HTMLElement>('[data-chat-turn]'));
    const cTop = c.getBoundingClientRect().top;
    const tops: number[] = [];
    const next = anchors.map((el) => {
      const top = el.getBoundingClientRect().top - cTop + c.scrollTop;
      tops.push(top);
      return { top, label: (el.getAttribute('data-chat-turn-label') || '').trim() };
    });
    topsRef.current = tops;
    setTicks(next);
    setScrollable(c.scrollHeight - c.clientHeight > 40);
    setWide(c.clientWidth >= RAIL_MIN_CHAT_WIDTH);
    updateActive();
  }, [scrollRef, updateActive]);

  useEffect(() => {
    const c = scrollRef.current;
    if (!c) return;
    const schedule = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        measure();
      });
    };
    measure();
    const onScroll = () => updateActive();
    c.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(schedule);
    ro.observe(c);
    const content = c.firstElementChild;
    if (content) ro.observe(content);
    const mo = new MutationObserver(schedule);
    mo.observe(c, { childList: true, subtree: true });
    return () => {
      c.removeEventListener('scroll', onScroll);
      ro.disconnect();
      mo.disconnect();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [measure, updateActive, scrollRef]);

  if (!scrollable || !wide || ticks.length < 2) return null;

  const jumpTo = (top: number) => scrollRef.current?.scrollTo({ top: Math.max(0, top - 16), behavior: 'smooth' });

  return (
    <div
      // Inside the chat, never the window: fixed to the viewport's edge it floated over whatever
      // panel sat right of a side chat (Source's right bar, owner 2026-09-29: 'nothing to do here').
      style={{ position: 'absolute', top: '50%', right: 16, transform: 'translateY(-50%)', zIndex: 20 }}
      onMouseEnter={() => setRailHovered(true)}
      onMouseLeave={() => {
        setRailHovered(false);
        setHovered(null);
      }}
    >
      {railHovered ? (
        // Expanded milestone panel: every sent prompt as a clickable row (jumps to
        // that turn). ChatGPT-matched: #353535 panel, radius 16, 6px 0 padding; rows
        // 14px, radius 10, 6px 10px; active row highlighted, hovered row brighter.
        <div
          className="no-scrollbar"
          style={{ display: 'flex', flexDirection: 'column', width: 320, maxHeight: '50vh', overflowY: 'auto', background: '#353535', borderRadius: 16, padding: '6px 0' }}
        >
          {ticks.map((tick, i) => {
            const isActive = i === active;
            const isHover = i === hovered;
            return (
              <button
                key={i}
                type="button"
                title={tick.label || undefined}
                onClick={() => jumpTo(tick.top)}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
                style={{
                  margin: '0 6px',
                  padding: '6px 10px',
                  borderRadius: 10,
                  border: 'none',
                  background: isHover ? '#4A4A4A' : isActive ? '#3F3F3F' : 'transparent',
                  color: '#ECECEC',
                  fontSize: 14,
                  lineHeight: '20px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  transition: 'background 120ms ease',
                  flexShrink: 0,
                }}
              >
                {tick.label || `Prompt ${i + 1}`}
              </button>
            );
          })}
        </div>
      ) : (
        // Resting ticks: one per turn, active wider + brighter.
        <div
          className="no-scrollbar"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, maxHeight: '50vh', overflow: 'hidden' }}
        >
          {ticks.map((tick, i) => {
            const emphasized = i === active;
            return (
              <button
                key={i}
                type="button"
                title={tick.label || undefined}
                onClick={() => jumpTo(tick.top)}
                style={{
                  height: 2,
                  width: emphasized ? 24 : 16,
                  borderRadius: 9999,
                  border: 'none',
                  padding: 0,
                  background: emphasized ? '#ECECEC' : '#6E6E6E',
                  cursor: 'pointer',
                  transition: 'width 150ms ease, background 150ms ease',
                  flexShrink: 0,
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const isSuppressedThinking = (step: ChatStateItem | undefined, suppressGenericThinking: boolean): boolean =>
  suppressGenericThinking && typeof step?.text === 'string' && step.text.trim() === 'Thinking';

interface ChatProps {
  runningScriptId: string | null;
  chatState?: ChatStateItem[];
  /** Master surface: ChatGPT-style image cards + right-side scroll rail. */
  chatGptSurface?: boolean;
}

export const Chat = memo(({
  runningScriptId,
  chatState = [],
  chatGptSurface = false,
}: ChatProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const canAccessSubjectTimelineView = useKernelStore((state) =>
    Boolean(state.userContext.permissions?.canAccessSubjectTimelineView)
  );
  const activeChatSessionId = useKernelStore((state) => state.activeChatSessionId);
  const activeSession = useKernelStore((state) => {
    const activeId = state.activeChatSessionId;
    return activeId ? state.inventory.ChatSession.registry[activeId] : null;
  });
  const setGlobalScrollTop = useKernelStore((state) => state.setGlobalScrollTop);
  const generationTasks = useKernelStore(selectChatProjectGenerationTasks);
  const chatProjectId = useKernelStore(selectActiveSessionProjectIdFromInventoryState);
  const isListProject = useKernelStore((state) => state.studioViewMode === 'marketing');
  const liveListTasks = useMemo(() => isListProject ? listLiveGenerationTasks(generationTasks, chatProjectId, activeChatSessionId) : EMPTY_GENERATION_TASKS,
    [isListProject, generationTasks, chatProjectId, activeChatSessionId]);
  const isSubmittingAITask = useKernelStore((state) => state.isSubmittingAITask);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  // The one source for pinning decisions, read when a trigger fires, never from a render's closure:
  // a stream captures its tick once, and an observer's frame lands after the user may have scrolled.
  const scrolledUpRef = useRef(false);
  // Only a move up marks the reader scrolled up: a pin's own scroll event can land after more growth was committed.
  const lastScrollTopRef = useRef(0);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const pinFrameRef = useRef<number | null>(null);

  // How long the agent has been running, shown beside the moving telemetry line.
  // Anchored on the EARLIEST unsettled step's own startTime (ChatStateItem carries
  // it already) so the clock measures the run, not the latest status change; the
  // ref survives step turnover and resets only when the agent goes idle.
  // A stranded lock (stream killed mid-script) must not read as work: the
  // send path heals it lazily on the next send, so the clock checks the
  // same liveness source instead of trusting the lock's existence.
  // A stop reads as stopped at once: the in-flight step keeps unwinding in the background until
  // it next checks the flag, and a new turn clears it.
  const isStopRequested = activeSession?.runtime?.isStopRequested === true;
  const isAgentRunning = !isStopRequested && agentIsWorking(
    chatState,
    runningScriptId && isScriptExecuting(runningScriptId) ? runningScriptId : null,
  );
  // State, not a ref: the label is computed during render, and the compiler
  // (rightly) refuses ref reads there. The set-in-effect is the sanctioned
  // transition write; the interval only forces the 1 Hz repaint.
  const [runStartedAt, setRunStartedAt] = useState<number | null>(null);
  const [, bumpRunClock] = useState(0);
  const lastSettledStatusIndex = chatState.map((step) => step.settled === true).lastIndexOf(true);
  const liveStatusStarts = chatState.slice(lastSettledStatusIndex + 1)
    .filter((step) => Number.isFinite(step.startTime)).map((step) => step.startTime);
  const liveStatusStartedAt = liveStatusStarts.length ? Math.min(...liveStatusStarts) : null;
  useEffect(() => {
    if (!isAgentRunning) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRunStartedAt(null);
      return;
    }
    const anchor = liveStatusStartedAt === null ? Date.now() : Math.min(Date.now(), liveStatusStartedAt);
    setRunStartedAt(anchor);
    const id = setInterval(() => bumpRunClock((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isAgentRunning, liveStatusStartedAt, activeChatSessionId]);
  const formatRunDuration = (s: number) => s < 60 ? `${s}s`
    : s < 3600 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
    : `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`;
  const runElapsedLabel = (() => {
    if (!isAgentRunning || runStartedAt === null) return '';
    // Live clock: the value must change on every paint, same rule as the ETA below.
    // eslint-disable-next-line react-hooks/purity
    return formatRunDuration(Math.max(0, Math.floor((Date.now() - runStartedAt) / 1000)));
  })();

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    setGlobalScrollTop(container.scrollTop);

    const { scrollTop, scrollHeight, clientHeight } = container;
    const movedUp = scrollTop < lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      scrolledUpRef.current = false;
      setShowScrollButton(false);
    } else if (movedUp) {
      scrolledUpRef.current = true;
      setShowScrollButton(true);
    }
  }, [setGlobalScrollTop]);

  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (container) {
      lastScrollTopRef.current = container.scrollTop;
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      scrolledUpRef.current = false;
      setShowScrollButton(false);
    }
  }, []);

  const pinToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (container && !scrolledUpRef.current) {
      container.scrollTop = container.scrollHeight;
      lastScrollTopRef.current = container.scrollTop;
    }
  }, []);

  useEffect(() => {
    scrolledUpRef.current = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing when this state settles is a behavioural change, not merge work
    setShowScrollButton(false);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      lastScrollTopRef.current = scrollRef.current.scrollTop;
    }
  }, [activeChatSessionId]);

  const requestedTimelineViewMode: ChatTimelineViewMode =
    activeSession?.runtime?.timelineViewMode === 'subject' ? 'subject' : 'session';
  const hasActiveSubject = Boolean(isEntityFocusSubject(activeSession?.activeSubject ?? null));
  const timelineViewMode: ChatTimelineViewMode =
    requestedTimelineViewMode === 'subject' && canAccessSubjectTimelineView && hasActiveSubject
      ? 'subject'
      : 'session';
  const {
    messages,
    hasMoreOlder: hasMoreOlderTimelineEvents,
    isLoadingOlder: isLoadingOlderTimelineEvents,
    loadOlder: loadOlderTimelineEvents,
  } = useTimelineFeedMessages(activeSession?.id ?? null, {
    scope: timelineViewMode,
    subject: activeSession?.activeSubject ?? null,
  });

  const displayMessages = useMemo(() => !isListProject ? messages : messages.filter(message => !(
    message.role === 'assistant' && message.agentName === 'Sourcing'
    && message.targetEntityType === 'Project' && message.targetEntityId
    && typeof message.content === 'string'
    && /^List generation attempt (\d+)\/(\d+)\nStarting attempt \1\/\2 with the same prompt, model and references\.$/.test(message.content)
  )), [isListProject, messages]);

  const subjectTimelineEntries = useMemo(() => {
    if (timelineViewMode !== 'session') return [] as (ChatSubjectFocusEntry & { timelineKey: string })[];
    if (!activeSession) return [] as (ChatSubjectFocusEntry & { timelineKey: string })[];
    const history = Array.isArray(activeSession?.subjectHistory) ? activeSession.subjectHistory : [];
    const fallbackHistory =
      history.length === 0 && activeSession?.activeSubject
        ? [toSubjectFocusHistoryEntry(
            activeSession.activeSubject,
            activeSession.updatedAt || new Date().toISOString()
          )]
        : [];
    const sourceHistory = history.length > 0 ? history : fallbackHistory;
    if (sourceHistory.length === 0) return [] as (ChatSubjectFocusEntry & { timelineKey: string })[];

    const normalized = sourceHistory
      .filter((entry): entry is ChatSubjectFocusEntry => {
        return Boolean(
          entry &&
          typeof entry.entityType === 'string' &&
          typeof entry.entityId === 'string' &&
          typeof entry.focusedAt === 'string'
        );
      })
      .filter((entry) => !Number.isNaN(new Date(entry.focusedAt).getTime()))
      .sort((a, b) => new Date(a.focusedAt).getTime() - new Date(b.focusedAt).getTime());

    const firstBySignature = new Map<string, ChatSubjectFocusEntry>();
    normalized.forEach((entry) => {
      const signature = buildSubjectFocusHistoryKey(entry);
      if (!signature) return;
      if (!firstBySignature.has(signature)) {
        firstBySignature.set(signature, entry);
      }
    });

    return Array.from(firstBySignature.entries()).map(([timelineKey, entry]) => ({
      ...entry,
      timelineKey,
    }));
  }, [activeSession, timelineViewMode]);

  const groupedTimeline = useMemo(() => {
    return [
      ...displayMessages.map(m => ({ type: 'message' as const, data: m, timestamp: m.timestamp })),
      ...subjectTimelineEntries.map(s => ({ type: 'subject' as const, data: s, timestamp: s.focusedAt }))
    ].sort((a, b) => {
      const tA = new Date(a.timestamp ?? 0).getTime();
      const tB = new Date(b.timestamp ?? 0).getTime();
      return tA - tB;
    });
  }, [displayMessages, subjectTimelineEntries]);

  const timelineEventMessageCount = useMemo(
    () => messages.filter((message) => typeof message.timelineEventId === 'string' && message.timelineEventId.length > 0).length,
    [messages]
  );
  const shouldShowLoadOlderTimelineEventsButton =
    (hasMoreOlderTimelineEvents || isLoadingOlderTimelineEvents) &&
    timelineEventMessageCount >= TIMELINE_FEED_LOAD_LIMIT;

  const latestUserMessageIsFactory = useMemo(() => {
    const latestUserMessage = [...messages].reverse().find(isUserLikeMessage);
    const content = latestUserMessage ? getMessageContentAsString(latestUserMessage) : '';
    return isFactoryCommandText(content);
  }, [messages]);
  const activeGenerationCount = useMemo(() => {
    return generationTasks.filter((task) => !task.error && !task.isOutcomeReported).length;
  }, [generationTasks]);
  const isGenerationRunning = isSubmittingAITask || activeGenerationCount > 0;
  // Factory owns no generic agent turn, but it still starts the normal live Thinking state before
  // its deterministic chat milestones arrive.
  const suppressGenericThinking = !isAgentRunning && !latestUserMessageIsFactory;
  const telemetrySteps = useMemo(
    () => {
      return chatState.filter((step, index) => {
      if (step.settled !== true && index <= lastSettledStatusIndex) return false;
      if (typeof step.text !== 'string' || step.text.trim().length === 0) return false;
      if (isSuppressedThinking(step, suppressGenericThinking)) return false;
      return true;
      });
    },
    [chatState, lastSettledStatusIndex, suppressGenericThinking],
  );

  useEffect(() => {
    if (!activeChatSessionId) return;
    const next = chatState.filter((step) => !isSuppressedThinking(step, suppressGenericThinking));
    if (next.length === chatState.length) return;
    kernel.current?.patchEntityRuntimeState('ChatSession', activeChatSessionId, { chatState: next });
  }, [activeChatSessionId, chatState, kernel, suppressGenericThinking]);
  const lastTelemetryStepText = telemetrySteps.length > 0
    ? telemetrySteps[telemetrySteps.length - 1]?.text?.trim() || ''
    : '';
  const [activeGenerationRun, setActiveGenerationRun] = useState<{
    startedAt: number;
    anchorMessageId: string | null;
  } | null>(null);
  const wasGenerationRunningRef = useRef(false);

  // Ticking clock so the ETA countdown below refreshes each second while a
  // generation runs (a determinate, "keep-entertained" affordance for slow
  // video renders). Idle when nothing is generating so it costs nothing.
  const [etaTick, setEtaTick] = useState(0);
  useEffect(() => {
    if (!isGenerationRunning) return;
    const iv = setInterval(() => setEtaTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [isGenerationRunning]);

  useEffect(() => {
    if (!wasGenerationRunningRef.current && isGenerationRunning) {
      setActiveGenerationRun({
        startedAt: Date.now(),
        anchorMessageId: null,
      });
    }

    if (wasGenerationRunningRef.current && !isGenerationRunning) {
      setActiveGenerationRun(null);
    }

    wasGenerationRunningRef.current = isGenerationRunning;
  }, [isGenerationRunning]);

  useEffect(() => {
    if (!activeGenerationRun || activeGenerationRun.anchorMessageId) return;

    const candidate = [...messages]
      .reverse()
      .find((message) => {
        if (isUserLikeMessage(message)) return false;
        const ts = Date.parse(message.timestamp || '');
        if (Number.isNaN(ts)) return false;
        return ts >= activeGenerationRun.startedAt - 5000;
      });
    if (!candidate?.timestamp) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing when this state settles is a behavioural change, not merge work
    setActiveGenerationRun((prev) => {
      if (!prev || prev.anchorMessageId) return prev;
      return { ...prev, anchorMessageId: candidate.timestamp };
    });
  }, [activeGenerationRun, messages]);

  const inlineGenerationTelemetry = useMemo(() => {
    if (isListProject) return null;
    if (isGenerationRunning && activeGenerationRun?.anchorMessageId) {
      // Determinate ETA from the running model's known average time. Slow video
      // renders (Seedance/Veo/Kling) otherwise show only an indeterminate shimmer;
      // this gives the user a live countdown so the wait feels bounded.
      const activeTask = generationTasks.find((t) => !t.error && !t.isOutcomeReported);
      const activeModel = (activeTask as { model?: string } | undefined)?.model;
      // Prefer the server-pushed ETA (registry average for the exact model) when the
      // socket has delivered it; fall back to the client-side estimate table.
      const socketEta = (activeTask as { loopEtaSeconds?: number | null } | undefined)?.loopEtaSeconds;
      const estimate = (typeof socketEta === 'number' && socketEta > 0 ? socketEta : null)
        ?? ((activeModel && designCommandTimeEstimations[activeModel]) || 0);
      // Date.now() in render is the point: this is a live countdown, so the value MUST change on
      // every paint. Memoising it would freeze the ETA at the first render.
      // eslint-disable-next-line react-hooks/purity
      const elapsed = Math.max(0, (Date.now() - activeGenerationRun.startedAt) / 1000);
      const remaining = estimate > 0 ? Math.max(0, estimate - elapsed) : 0;
      const eta = estimate > 0 ? (remaining > 4 ? ` · ~${Math.ceil(remaining)}s left` : (elapsed > 2 ? ' · finishing up…' : '')) : '';
      // Claude-Code-style stage label: name the self-validation tier the loop is on.
      // Derive it straight from the running model so it shows the instant a video task
      // is active, don't wait on the film-route RUNNING socket (which may lag or, for
      // a stalled agent flow, never arrive). Fall back to the socket-pushed label.
      const tierLabel =
        videoLoopStageLabel(activeModel)
        || ((activeTask as { loopStepLabel?: string | null } | undefined)?.loopStepLabel || '');

      if (lastTelemetryStepText.length > 0) {
        return { text: `${lastTelemetryStepText}${eta}`, pulsing: true };
      }
      if (tierLabel) {
        return { text: `${tierLabel}${eta}`, pulsing: true };
      }
      if (isSubmittingAITask && activeGenerationCount === 0) {
        return { text: 'Preparing generation...', pulsing: true };
      }
      // "Generating 3 layers..." only repeated what the command trace under the message already says
      // (owner, 2026-09-28); what it adds is the time left, so that alone stays.
      const etaOnly = eta.replace(/^ · /, '');
      return etaOnly ? { text: etaOnly.charAt(0).toUpperCase() + etaOnly.slice(1), pulsing: true } : null;
    }

    return null;
    // etaTick drives the per-second refresh of the countdown.
  }, [isListProject, isGenerationRunning, activeGenerationRun, lastTelemetryStepText, isSubmittingAITask, activeGenerationCount, generationTasks, etaTick]);
  const activeGenerationAnchorTimelineIndex = useMemo(() => {
    if (!activeGenerationRun?.anchorMessageId) return -1;
    for (let i = groupedTimeline.length - 1; i >= 0; i -= 1) {
      const item = groupedTimeline[i];
      if (!item || item.type !== 'message') continue;
      if (item.data.timestamp === activeGenerationRun.anchorMessageId) return i;
    }
    return -1;
  }, [groupedTimeline, activeGenerationRun]);

  // Any growth (message, trace line, telemetry row, image load) resizes the always-mounted content wrapper,
  // so this observer is the only content trigger.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const resizeObserver = new ResizeObserver(() => {
      if (pinFrameRef.current != null) return;
      pinFrameRef.current = requestAnimationFrame(() => {
        pinFrameRef.current = null;
        pinToBottom();
      });
    });
    resizeObserver.observe(container);
    if (scrollContentRef.current) resizeObserver.observe(scrollContentRef.current);
    return () => {
      resizeObserver.disconnect();
      if (pinFrameRef.current != null) cancelAnimationFrame(pinFrameRef.current);
      pinFrameRef.current = null;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!scrollRef.current || isInertSurface(scrollRef.current)) return;
      if (event.key !== 'PageDown' && event.key !== 'PageUp') return;

      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) {
        return;
      }

      event.preventDefault();
      const pageHeight = scrollRef.current.clientHeight;
      const direction = event.key === 'PageDown' ? 1 : -1;
      scrollRef.current.scrollBy({
        top: direction * pageHeight,
        behavior: 'smooth',
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);


  const openTechnicalPromptKeys = activeSession?.runtime?.openTechnicalPromptKeys;
  const handleContextMenu = useCallback((
    event: React.MouseEvent<HTMLDivElement>,
    message: Message,
    messageKey: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    kernel.current?.openChatMessageMenu({
      position: { x: event.clientX, y: event.clientY },
      message,
      messageKey,
      userLike: isUserLikeMessage(message),
      imageSrc: contextMenuImageSrc(event.target as HTMLElement | null),
    });
  }, [kernel]);

  const renderEmptyState = () => (
    <div className="flex items-center justify-center h-full">
      <FaterLogo className="mb-4 w-[4vmin] h-[4vmin] text-white/25" />
    </div>
  );

  // Master (ChatGPT-style) surface anchors the conversation to the TOP so a new
  // session starts at the top and grows down, exactly like ChatGPT. Other chat
  // surfaces keep the messaging-app bottom anchor.
  const scrollContentClass = chatGptSurface
    ? 'flex min-h-full w-full flex-col justify-start pb-4 pt-4'
    : 'flex min-h-full w-full flex-col justify-end pb-4 pt-4';
  // Master surface: the scroll container is full-width (so the gutters scroll), so the
  // content centers itself at a fixed width here. Other surfaces keep the old wrapper.
  const outerWrapperClass = chatGptSurface ? 'w-full px-4' : 'w-full pl-[5px] pr-0';
  // ONE GAP FOR EVERY ENTRY. Measured in the browser on 2026-08-16: the flex gap is uniform at 8px
  // and no entry carries a margin of its own, so this is NOT where the uneven spacing came from.
  // Changing it here moves messages and traces together and preserves any difference between them,
  // which is why the visible gap has to be fixed on the row that is too tall, not on this stack.
  const innerStackClass = chatGptSurface
    ? 'mx-auto w-full max-w-[880px] flex flex-col gap-2'
    : 'mx-auto w-full max-w-6xl flex flex-col gap-2';
  // `empty:hidden` on the wrapper, because a message that renders NOTHING must not occupy a slot.
  // The bubble inside is already dropped when it has no content, but the wrapper around it stayed,
  // and an empty flex child still takes its share of the gap: two command rows ended up 32px apart
  // where two paragraphs sit 8, which is the uneven spacing reported on 2026-08-16. Hiding it here
  // rather than filtering the list keeps ONE definition of "this message shows something", inside
  // the component that knows what it can show.
  const userEntryClass = 'w-full max-w-[75%] empty:hidden';
  const agentEntryClass = 'w-full max-w-[96%] empty:hidden';

  return (
    <ChatImageSurfaceContext.Provider value={chatGptSurface}>
      <div
        ref={scrollRef}
        className="absolute inset-0 overflow-y-auto custom-scrollbar"
        onScroll={handleScroll}
        style={{
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0px, black 22px, black 100%)',
          maskImage: 'linear-gradient(to bottom, transparent 0px, black 22px, black 100%)',
        }}
      >
        <div ref={scrollContentRef} className={scrollContentClass}>
          {(groupedTimeline.length > 0 || telemetrySteps.length > 0 || liveListTasks.length > 0) && (
            <div className={outerWrapperClass}>
              <div className={innerStackClass}>
                {shouldShowLoadOlderTimelineEventsButton && (
                  <div className="flex w-full justify-center pb-2">
                    <button
                      type="button"
                      onClick={() => {
                        void loadOlderTimelineEvents();
                      }}
                      disabled={isLoadingOlderTimelineEvents}
                      className={clsx(
                        'rounded-md border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] transition-colors',
                        isLoadingOlderTimelineEvents
                          ? 'cursor-default text-white/45'
                          : 'text-white/65 hover:bg-white/[0.08] hover:text-white/85'
                      )}
                    >
                      {isLoadingOlderTimelineEvents ? t.loadingOlderEvents : t.loadOlderEvents}
                    </button>
                  </div>
                )}
                {(() => {
                  // Pre-process: accumulate scripts from consecutive empty messages
                  const elements: React.ReactNode[] = [];
                  let pendingScripts: ScriptInstance[] = [];
                  let pendingKey = '';

                  // `elements` and `pendingScripts` are locals this builder fills and returns. The
                  // compiler flags the push/reset as mutation during render; nothing outside this
                  // function can observe either array before it is done.
                  const flushPendingScripts = (isLast: boolean) => {
                    if (pendingScripts.length === 0) return;
                    const webBrowsing = buildWebBrowsingFromScripts(pendingScripts, runningScriptId);
                    elements.push(
                      <div key={`trace-${pendingKey}`} className={clsx(agentEntryClass, 'self-start')}>
                        {webBrowsing && <WebBrowsingSegment state={webBrowsing} />}
                        <BrandResearchResult scripts={pendingScripts} />
                        <CommandTrace
                          scripts={pendingScripts}
                          runningScriptId={runningScriptId}
                          isLast={isLast}
                          stopRequested={isStopRequested}
                        />
                      </div>
                    );
                    pendingScripts = [];
                    pendingKey = '';
                  };

                  // A LINE THAT DESCRIBES FINISHED WORK IS PART OF THE CONVERSATION, NOT A STATUS
                  // BAR. All telemetry used to be pushed after every message, so "Scored 5 of 5
                  // brands" sat glued above the composer and stayed there while the user asked two
                  // more questions underneath it - the run's outcome kept sliding down, out of the
                  // moment it belonged to. Reported on 2026-08-16: "it should stack up, never stick
                  // to the bottom."
                  //
                  // So a SETTLED line is emitted at the point in the transcript where it happened,
                  // and everything after it stacks below. Only a line still MOVING stays last,
                  // which is right: it describes what is happening now.
                  const telemetryFontFamily = TRACE_FONT;
                  const renderTelemetryRow = (step: ChatStateItem, key: string, animate: boolean) => (
                    <div key={key} className={clsx(agentEntryClass, animate && step.brands?.length ? 'self-stretch' : 'self-start', 'min-w-0')} data-agent-activity={animate ? 'live' : 'settled'}>
                      {/* A settled line is a receipt, drawn as the other receipts (CommandTrace, the web
                          search line): 14px glyph, 14px #8E8E8E. Only the live line keeps the
                          message size, as ChatGPT's "Thinking". */}
                      <div
                        className={clsx('flex min-w-0 items-center gap-2 py-0 text-[11px] leading-tight', !animate && 'my-1')}
                        style={{ fontFamily: telemetryFontFamily, fontSize: animate ? (chatGptSurface ? 16 : undefined) : 14, lineHeight: animate ? (chatGptSurface ? '26px' : undefined) : '20px' }}
                      >
                        {animate && !step.ambient && !step.brands?.length ? <SourceFocusMark /> : null}
                        {animate && step.brands?.length ? (
                          // THE SENTENCE ABOVE THE RIBBON, not behind it: the ribbon alone showed
                          // logos scrolling and never said which brand was being worked on
                          // (owner, 2026-09-27).
                          <div className="flex min-w-0 flex-1 flex-col gap-1.5 px-1 py-1.5">
                            <BrandLogoCarousel brands={step.brands} label={formatActivityText(step.text)} />
                            <span className="thinking-text-animated min-w-0 truncate" title={formatActivityText(step.text)} style={{ fontSize: 'inherit' }}>{formatActivityText(step.text)}</span>
                          </div>
                        ) : animate ? (
                          <span className="thinking-text-animated min-w-0 truncate" title={formatActivityText(step.text)} style={{ fontSize: 'inherit' }}>{formatActivityText(step.text)}</span>
                        ) : (
                          <>
                            <Check size={14} strokeWidth={2} className="shrink-0" style={{ color: '#8E8E8E' }} aria-hidden />
                            <span className="min-w-0 truncate" style={{ color: '#8E8E8E', fontSize: 'inherit' }}>
                              {formatActivityText(step.text)}
                            </span>
                          </>
                        )}
                        {animate && runElapsedLabel && <span className="shrink-0" style={{ color: '#777777', fontVariantNumeric: 'tabular-nums' }}>{runElapsedLabel}</span>}
                      </div>
                    </div>
                  );

                  // A Factory run starts image work before its assistant reply exists. Keep the
                  // live Thinking line visible in that gap; once a reply gives generation a home,
                  // the inline status takes over instead of duplicating it beneath the transcript.
                  const showTelemetry = telemetrySteps.length > 0 && (!isGenerationRunning || !inlineGenerationTelemetry);
                  const settledSteps = telemetrySteps.filter((step) => step.settled === true);
                  const liveStep = showTelemetry && !isStopRequested ? telemetrySteps.filter((step) => step.settled !== true).at(-1) : undefined;
                  let settledCursor = 0;
                  // Emit every settled line whose moment has passed, before the item that follows it.
                  const flushSettledTelemetryUpTo = (until: number) => {
                    while (settledCursor < settledSteps.length && settledSteps[settledCursor]!.startTime <= until) {
                      // The steps that produced this outcome may still be buffered: traces
                      // accumulate across messages and flush when the next one with content
                      // arrives. Emitting the line first put "Scored 5 of 5 brands" ABOVE the
                      // commands that scored them.
                      flushPendingScripts(false);
                      const step = settledSteps[settledCursor]!;
                      elements.push(renderTelemetryRow(step, `telemetry-settled-${settledCursor}-${step.startTime}`, false));
                      settledCursor += 1;
                    }
                  };

                  // Codex's "Worked for": once a run that did real work has settled, its final message is preceded by
                  // how long the agent worked, so the user sees that it stopped and reads the conclusion as the end.
                  const workedFor = new Map<number, string>();
                  {
                    let runStart: number | null = null, finalIndex = -1, steps = 0;
                    const close = (live: boolean) => {
                      const end = finalIndex >= 0 ? Date.parse(groupedTimeline[finalIndex]!.timestamp ?? '') : NaN;
                      if (!live && runStart !== null && steps > 1 && Number.isFinite(end) && end > runStart) workedFor.set(finalIndex, formatRunDuration(Math.round((end - runStart) / 1000)));
                    };
                    groupedTimeline.forEach((entry, i) => {
                      if (entry.type !== 'message') return;
                      if (isUserLikeMessage(entry.data)) {
                        close(false);
                        const at = Date.parse(entry.timestamp ?? '');
                        runStart = Number.isFinite(at) ? at : null; finalIndex = -1; steps = 0;
                        return;
                      }
                      steps += 1 + (entry.data.scriptsInstances?.length ?? 0);
                      if ((entry.data.content || '').trim()) finalIndex = i;
                    });
                    close(isAgentRunning);
                  }

                  const messageKey = createChatMessageKeyer();
                  for (let index = 0; index < groupedTimeline.length; index++) {
                    const item = groupedTimeline[index]!;
                    const itemAt = Date.parse(item.timestamp ?? '');
                    if (Number.isFinite(itemAt)) flushSettledTelemetryUpTo(itemAt);

                    if (item.type === 'subject') {
                      // Hidden by product decision: keep timeline computation intact,
                      // but do not render subject-focus blocks in chat.
                      flushPendingScripts(false);
                      continue;
                    }

                    const message = item.data;
                    const messageId = messageKey(message);
                    const isUserMessage = isUserLikeMessage(message);
                    const hasContent = (message.content || '').trim().length > 0;
                    const visibleScripts = (message.scriptsInstances ?? []).filter(script =>
                      isFactoryReferenceLookupScript(script.commands)
                        ? script.commands.some(command => command.status === 'failed')
                        : script.studioViewMode !== 'marketing');
                    const hasScripts = visibleScripts.some(script => script.commands.length > 0);

                    // The Designer compacted the conversation here: what came before stays on screen, the agent reads it as a summary.
                    // A turn that started by compacting draws it above its user message; /compact's own message where it sits.
                    // A compaction the provider made mid-turn (not the first item) is the agent's housekeeping and draws nothing.
                    const compactedHere = isUserMessage
                      ? compactionOf(groupedTimeline.slice(index + 1).find(entry => entry.type === 'message')?.data) === 'turn'
                      : compactionOf(message) === 'command';
                    if (compactedHere) {
                      flushPendingScripts(false);
                      elements.push(
                        <div
                          key={`compacted-${messageId}`}
                          className="flex w-full items-center gap-3 self-stretch"
                          data-testid="conversation-compacted"
                          title={lang === 'fr' ? 'Les messages plus haut restent visibles ; le Designer les lit sous forme de résumé.' : 'The messages above stay here for you; the Designer reads them as a summary.'}
                        >
                          <div className="h-px flex-1 bg-white/[0.08]" />
                          <span className="shrink-0 text-[#8E8E8E]" style={COMPACTED_LABEL_STYLE}>{lang === 'fr' ? 'Conversation compactée' : 'Conversation compacted'}</span>
                          <div className="h-px flex-1 bg-white/[0.08]" />
                        </div>
                      );
                    }

                    if (hasContent || isUserMessage) {
                      // Flush any accumulated scripts before this message
                      flushPendingScripts(false);
                      const worked = workedFor.get(index);
                      if (worked) elements.push(
                        <div key={`worked-${messageId}`} className={clsx(agentEntryClass, 'flex items-center gap-3 self-start')} data-testid="run-worked-for">
                          <span className="shrink-0 text-[#8E8E8E]" style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{lang === 'fr' ? 'A travaillé pendant' : 'Worked for'} {worked}</span>
                          <div className="h-px flex-1 bg-white/[0.08]" />
                        </div>
                      );

                      const messageAlignmentClass = isUserMessage ? 'self-end' : 'self-start';
                      const messageWidthClass = isUserMessage ? userEntryClass : agentEntryClass;
                      elements.push(
                        <div
                          key={`msg-wrapper-${messageId}`}
                          className={clsx(messageWidthClass, messageAlignmentClass)}
                          {...(isUserMessage
                            ? { 'data-chat-turn': '1', 'data-chat-turn-label': (message.content || '').slice(0, 80) }
                            : {})}
                        >
                          <ChatMessage
                            message={message}
                            messageId={messageId}
                            onContextMenu={handleContextMenu}
                            onStreamingTick={pinToBottom}
                            inlineTelemetryText={
                              index === activeGenerationAnchorTimelineIndex
                                ? (inlineGenerationTelemetry?.text ?? null)
                                : null
                            }
                            inlineTelemetryPulsing={
                              index === activeGenerationAnchorTimelineIndex && !!inlineGenerationTelemetry?.pulsing
                            }
                            technicalPromptOpen={Boolean(message.technicalPrompt) && Boolean(openTechnicalPromptKeys?.includes(messageId))}
                          />
                        </div>
                      );
                    }

                    for (const script of message.scriptsInstances ?? []) {
                      for (const command of script.commands) {
                        const proposal = command.action_type === 'PROPOSE_SOURCING_BUDGET' && command.status === 'success'
                          ? readSourceBudgetProposal(command.parameters.result) : null;
                        if (proposal) elements.push(<div key={`budget-${proposal.id}`} className={`${agentEntryClass} min-w-0 self-start`}>
                          <SourceBudgetProposalCard initial={proposal} />
                        </div>);
                      }
                    }

                    if (isListProject) for (const script of message.scriptsInstances ?? []) {
                      for (const command of script.commands) {
                        if (command.status === 'success' && ['LIST_FACTORY_FOLDERS', 'SHOW_FACTORY_REFERENCES'].includes(command.action_type)) {
                          elements.push(<div key={`factory-${messageId}-${script.id}-${command.id}`} className={`${agentEntryClass} min-w-0 self-start`}>
                            <FactoryReferenceResults kind={command.action_type} result={command.parameters.result} sessionId={activeChatSessionId!} />
                          </div>);
                        }
                      }
                    }

                    // Accumulate scripts (whether message had content or not)
                    if (hasScripts) {
                      if (!pendingKey) pendingKey = messageId as string;
                      pendingScripts = [...pendingScripts, ...visibleScripts];
                    }

                    // If the NEXT item is a message with content (or end of timeline), flush scripts
                    const next = groupedTimeline[index + 1];
                    const nextHasContent = next?.type === 'message' && (next.data.content || '').trim().length > 0;
                    const nextIsUser = next?.type === 'message' && isUserLikeMessage(next.data);
                    const nextIsSubject = next?.type === 'subject';
                    const isEnd = index === groupedTimeline.length - 1;

                    if (pendingScripts.length > 0 && (nextHasContent || nextIsUser || nextIsSubject || isEnd)) {
                      flushPendingScripts(isEnd);
                    }
                  }

                  // Final flush
                  flushPendingScripts(true);

                  // Anything settled after the last message, then whatever is still moving.
                  flushSettledTelemetryUpTo(Number.POSITIVE_INFINITY);
                  if (liveStep) elements.push(renderTelemetryRow(liveStep, 'telemetry-live', true));

                  return elements;
                })()}
                {isListProject && <ListGenerationTelemetry tasks={liveListTasks} />}
              </div>
            </div>
          )}
        </div>
      </div>

      {messages.length === 0 && telemetrySteps.length === 0 && liveListTasks.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {renderEmptyState()}
        </div>
      )}

      <button
        onClick={scrollToBottom}
        className={clsx(
          "absolute z-50 transition-all duration-200 p-2",
          "bottom-4 right-6 text-white/40 hover:text-white",
          showScrollButton ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"
        )}
        aria-label="Scroll to bottom"
      >
        <ChevronDown size={16} />
      </button>

      {chatGptSurface && <ChatScrollRail scrollRef={scrollRef} />}
    </ChatImageSurfaceContext.Provider>
  );
});

Chat.displayName = 'Chat';
